import { macroEnergyGap, macroEnergyPercentage } from '@/utils/macro-energy';
import { formatStructuredClinicalLimit } from '@/clients/details/v2/components/analysis/clinicalTargetSemantics';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LabeledInput } from '@/components/shared/LabeledInput';
import { Button } from '@/components/ui/button';
import { MultiSelect, type Option } from '@/components/ui/multi-select';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Target, Edit, Save, X, Loader2, ChevronDown, ChevronUp, AlertTriangle, Flag, Ban, Coffee, Info } from 'lucide-react';
import { Client } from '@/types/client';
import { getValidAllergies, getValidCuisineTypes, getValidFoodIntolerances, getValidPreferences } from '@/lib/clientPreferenceOptions';
import { calculationNutritionalTargets, updateClient } from '@/lib/apiService';
import { toFoodIntoleranceApiLabels } from '@/utils/food-intolerances';
import { showErrorToast, showSuccessToast, showWarningToast } from '@/utils/toast-utils';
import { isIomUsaFormula, isEfsaEuFormula, isSacnFormula, isIcmrIndiaFormula, isAusNzNrvFormula, isNordicNnrFormula, getNutrientConfig } from '@/utils/nutrition-engine/iomUsaFormulaConfig';
import { 
  evaluateClientGoal, 
  extractClientGoalGrams, 
  extractClientGoalKcal,
  extractAmdrValues,
  extractReferenceValue,
  extractUlValue,
  EvaluationInput 
} from '@/utils/nutrition-engine/evaluateClientGoal';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tag } from '@/components/ui/tag';
import { validateMealPlanCalorieOverride } from '@/utils/meal-plan-calorie-override-validation';
import { resolveClientTargetCalories } from '@/utils/client-goals';
import { getMacroGoalHint, type MacroGoalNutrient } from '@/utils/macro-goal-guidance';

interface DietaryIntakeGoalsProps {
  clientId: string;
  client?: Client;
  refreshClientData?: () => Promise<void>;
  hideSaveButtons?: boolean;
  compactView?: boolean;
  onGoalsEditOpen?: () => void;
  onPreferencesEditOpen?: () => void;
  onGoalsSaved?: () => void;
  onPreferencesSaved?: () => void;
}

const dietaryPreferencesTooltipText =
  'These preferences are used only for the next meal generation and can be adjusted again anytime.';

const MODAL_MACRO_KCAL_PER_GRAM = {
  protein: 4,
  carbohydrates: 4,
  totalFat: 9,
  dietaryFibre: 2,
} as const;

export interface DietaryIntakeGoalsRef {
  saveDietaryGoals: () => Promise<any>;
  getCurrentValues: () => any;
  resetToOriginalValues: () => void;
}

type GoalValue = { value: number | null; unit: string };

type MacroGoalValues = {
  grams?: GoalValue;
  kcal?: GoalValue;
};

type CarbohydrateGoalValues = {
  energyPercent?: GoalValue;
  kcal?: GoalValue;
  grams?: GoalValue;
};

type EditedClientGoalsState = {
  protein?: MacroGoalValues;
  carbohydrates?: CarbohydrateGoalValues;
  dietaryFibre?: GoalValue;
  totalFat?: MacroGoalValues;
  saturatedFat?: MacroGoalValues;
  water?: GoalValue;
};

export const DietaryIntakeGoals = forwardRef<DietaryIntakeGoalsRef, DietaryIntakeGoalsProps>(({
  clientId,
  client,
  refreshClientData,
  hideSaveButtons = false,
  compactView = false,
  onGoalsEditOpen,
  onPreferencesEditOpen,
  onGoalsSaved,
  onPreferencesSaved,
}, ref) => {
  const parseOptionalNumber = (value: unknown): number | undefined => {
    if (value === null || value === undefined || value === '') return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  // Internal state for edit mode and data
  const [isEditMode, setIsEditMode] = useState(false);
  const [isGoalsModalOpen, setIsGoalsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPreferencesSaving, setIsPreferencesSaving] = useState(false);
  const [editMode, setEditMode] = useState<'goals' | null>(null);
  const [editedFields, setEditedFields] = useState<Set<string>>(new Set());
  const [isExpanded, setIsExpanded] = useState(hideSaveButtons || compactView);
  const [warnings, setWarnings] = useState<Array<{
    field?: string;
    message: string;
    minimumRequired?: number;
    actualValue?: number;
    canOverride?: boolean;
    warningType?: string;
    errorCode?: string;
    meta?: any;
  }>>([]);
  
  // Separate state for preferences card
  const [isPreferencesEditMode, setIsPreferencesEditMode] = useState(false);
  const [isPreferencesModalOpen, setIsPreferencesModalOpen] = useState(false);
  const [preferencesEditedFields, setPreferencesEditedFields] = useState<Set<string>>(new Set());
  const [cuisineModalInput, setCuisineModalInput] = useState('');
  const [allergyModalInput, setAllergyModalInput] = useState('');
  const [preferenceModalInput, setPreferenceModalInput] = useState('');
  const [intoleranceModalInput, setIntoleranceModalInput] = useState('');
  const [showCuisineSuggestions, setShowCuisineSuggestions] = useState(false);
  const [showAllergySuggestions, setShowAllergySuggestions] = useState(false);
  const [showPreferenceSuggestions, setShowPreferenceSuggestions] = useState(false);
  const [showIntoleranceSuggestions, setShowIntoleranceSuggestions] = useState(false);
  // State for editing individual clientGoals values
  const [editingClientGoal, setEditingClientGoal] = useState<string | null>(null);
  const [editedClientGoals, setEditedClientGoals] = useState<EditedClientGoalsState>({});
  // Note: Select values no longer needed with MultiSelect component
  const [clientGoal, setClientGoal] = useState<{
    proteinGoalMin?: number | string;
    proteinGoalMax?: number | string;
    carbsGoalMin?: number | string;
    carbsGoalMax?: number | string;
    fatGoalMin?: number | string;
    fatGoalMax?: number | string;
    fiberGoalGrams?: number | string;
    waterGoalLiters?: number | string;
    eerGoalCalories?: number | string;
    cuisineType?: string[];
    allergies?: string[];
    preferences?: string[];
    foodIntolerances?: string[];
  }>({});

  const deepCopyGoalValue = (obj: any): any => {
    if (obj === null || obj === undefined) return obj;
    return JSON.parse(JSON.stringify(obj));
  };

  // Store original values when entering edit mode
  const originalValuesRef = useRef<typeof clientGoal>({});
  const originalEditedClientGoalsRef = useRef<EditedClientGoalsState>({});
  const originalMacroCalorieGoalRef = useRef<number | null>(null);
  const eerRecalculateDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestEerRecalculateRequestIdRef = useRef(0);
  const shouldAutoRecalculateEerRef = useRef(false);

  const clearPendingEerRecalculation = () => {
    latestEerRecalculateRequestIdRef.current += 1;
    if (eerRecalculateDebounceRef.current) {
      clearTimeout(eerRecalculateDebounceRef.current);
      eerRecalculateDebounceRef.current = null;
    }
  };

  useEffect(() => {
    setEditedClientGoals({});
    originalEditedClientGoalsRef.current = {};
  }, [clientId]);

  // Initialize data from client
  useEffect(() => {
    if (client) {
      const goals = {
        eerGoalCalories: resolveClientTargetCalories(client) ?? client.eerOverride ?? client.eer ?? '',
        proteinGoalMin: client.clientGoals?.protein?.grams?.value || '',
        proteinGoalMax: client.clientGoals?.protein?.grams?.value || '',
        carbsGoalMin: client.clientGoals?.carbohydrates?.grams?.value || '',
        carbsGoalMax: client.clientGoals?.carbohydrates?.grams?.value || '',
        fatGoalMin: client.clientGoals?.totalFat?.grams?.value || '',
        fatGoalMax: client.clientGoals?.totalFat?.grams?.value || '',
        fiberGoalGrams: client.clientGoals?.dietaryFibre?.value || '',
        waterGoalLiters: client.clientGoals?.water?.value || '',
        // Read from client object directly (new API format)
        cuisineType: client.cuisineTypesPreferred || [],
        allergies: client.allergies || [],
        preferences: client.dietaryPreferences || [],
        foodIntolerances: client.foodIntolerances || [],
      };
      // Store original values for fallback on cancel
      originalValuesRef.current = { ...goals };
      setClientGoal(goals);
    }
  }, [client]);

  useEffect(() => {
    if (compactView) {
      setIsExpanded(true);
    }
  }, [compactView]);

  // Recalculate macros from total calories using protein/4, carbs/4, fat/9 for grams
  const recalculateMacrosFromCalories = (totalCalories: number) => {
    if (totalCalories <= 0 || !Number.isFinite(totalCalories)) return;

    const fallback = client?.clientGoals;
    const getKcal = (source: any, nutrient: string) => {
      const val =
        source?.[nutrient]?.kcal?.value ??
        (fallback as any)?.[nutrient]?.kcal?.value;
      return typeof val === 'number' && Number.isFinite(val) ? val : null;
    };

    let newProteinG: number, newCarbG: number, newFatG: number;

    setEditedClientGoals((prev) => {
      const proteinKcal = getKcal(prev, 'protein');
      const carbKcal = getKcal(prev, 'carbohydrates');
      const fatKcal = getKcal(prev, 'totalFat');
      const totalCurrent = (proteinKcal ?? 0) + (carbKcal ?? 0) + (fatKcal ?? 0);

      let pctProtein: number, pctCarb: number, pctFat: number;
      if (totalCurrent > 0) {
        pctProtein = (proteinKcal ?? 0) / totalCurrent;
        pctCarb = (carbKcal ?? 0) / totalCurrent;
        pctFat = (fatKcal ?? 0) / totalCurrent;
      } else {
        pctProtein = 0.25;
        pctCarb = 0.5;
        pctFat = 0.25;
      }

      const newProteinKcal = Math.round(totalCalories * pctProtein);
      const newCarbKcal = Math.round(totalCalories * pctCarb);
      const newFatKcal = Math.round(totalCalories * pctFat);
      newProteinG = Math.round((newProteinKcal / 4) * 10) / 10;
      newCarbG = Math.round((newCarbKcal / 4) * 10) / 10;
      newFatG = Math.round((newFatKcal / 9) * 10) / 10;

      return {
        ...prev,
        protein: {
          ...(prev.protein as any),
          grams: { value: newProteinG, unit: 'g' },
          kcal: { value: newProteinKcal, unit: 'kcal' },
        },
        carbohydrates: {
          ...(prev.carbohydrates as any),
          grams: { value: newCarbG, unit: 'g' },
          kcal: { value: newCarbKcal, unit: 'kcal' },
        },
        totalFat: {
          ...(prev.totalFat as any),
          grams: { value: newFatG, unit: 'g' },
          kcal: { value: newFatKcal, unit: 'kcal' },
        },
      };
    });

    // Also sync legacy clientGoal so all displays stay in sync
    setClientGoal((prev) => ({
      ...prev,
      proteinGoalMin: newProteinG,
      proteinGoalMax: newProteinG,
      carbsGoalMin: newCarbG,
      carbsGoalMax: newCarbG,
      fatGoalMin: newFatG,
      fatGoalMax: newFatG,
    }));
  };

  // Handle data changes
  const handleDataChange = (field: string, value: any) => {
    const newValue = value === '' || value === undefined || (typeof value === 'number' && !Number.isFinite(value))
      ? null
      : value;
    setClientGoal(prev => ({ ...prev, [field]: newValue }));
    
    // Track edited fields
    setEditedFields(prev => new Set(prev).add(field));

    // Do not auto-update protein, carb, or total fat when editing calories;
    // those only change when the user clicks "Recalculate Macros".
  };

  const parseNullableNumber = (raw: string): number | null => {
    if (raw === '') return null;
    const parsed = parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const toNullableNumber = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value === 'string') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  };

  const buildGoalsUpdatePayload = (goalOverrides?: EditedClientGoalsState) => {
    const currentClientGoals = client?.clientGoals || {};
    const sourceGoals = goalOverrides ?? editedClientGoals;

    const resolvedProtein = sourceGoals.protein ?? currentClientGoals.protein;
    const resolvedCarbs = sourceGoals.carbohydrates ?? currentClientGoals.carbohydrates;
    const resolvedFibre = sourceGoals.dietaryFibre ?? currentClientGoals.dietaryFibre;
    const resolvedTotalFat = sourceGoals.totalFat ?? currentClientGoals.totalFat;
    const resolvedSatFat = sourceGoals.saturatedFat ?? currentClientGoals.saturatedFat;
    const resolvedWater = sourceGoals.water ?? currentClientGoals.water;

    const eerOverride = parseOptionalNumber(clientGoal.eerGoalCalories);
    const effectiveEer =
      eerOverride
      ?? parseOptionalNumber(client?.eerOverride)
      ?? parseOptionalNumber(client?.eer);

    const resolvedCarbsKcal = toNullableNumber((resolvedCarbs as any)?.kcal?.value);
    const explicitCarbsEnergyPercent = toNullableNumber((resolvedCarbs as any)?.energyPercent?.value);
    const derivedCarbsEnergyPercent =
      explicitCarbsEnergyPercent !== null
        ? explicitCarbsEnergyPercent
        : (effectiveEer !== undefined && effectiveEer > 0 && resolvedCarbsKcal !== null)
            ? Math.round((resolvedCarbsKcal / effectiveEer) * 1000) / 10
            : null;

    const updatePayload: any = {
      clientGoalsPatch: {
        protein: {
          grams: {
            value:
              toNullableNumber((resolvedProtein as any)?.grams?.value)
              ?? toNullableNumber((resolvedProtein as any)?.value),
            unit: (resolvedProtein as any)?.grams?.unit || 'g',
          },
          kcal: {
            value: toNullableNumber((resolvedProtein as any)?.kcal?.value),
            unit: (resolvedProtein as any)?.kcal?.unit || 'kcal',
          },
        },
        carbohydrates: {
          energyPercent: {
            value: derivedCarbsEnergyPercent,
            unit: (resolvedCarbs as any)?.energyPercent?.unit || '% E',
          },
          kcal: {
            value: resolvedCarbsKcal,
            unit: (resolvedCarbs as any)?.kcal?.unit || 'kcal',
          },
          grams: {
            value:
              toNullableNumber((resolvedCarbs as any)?.grams?.value)
              ?? toNullableNumber((resolvedCarbs as any)?.value),
            unit: (resolvedCarbs as any)?.grams?.unit || 'g',
          },
        },
        dietaryFibre: {
          value: toNullableNumber((resolvedFibre as any)?.value),
          unit: (resolvedFibre as any)?.unit || 'g',
        },
        totalFat: {
          grams: {
            value:
              toNullableNumber((resolvedTotalFat as any)?.grams?.value)
              ?? toNullableNumber((resolvedTotalFat as any)?.value),
            unit: (resolvedTotalFat as any)?.grams?.unit || 'g',
          },
          kcal: {
            value: toNullableNumber((resolvedTotalFat as any)?.kcal?.value),
            unit: (resolvedTotalFat as any)?.kcal?.unit || 'kcal',
          },
        },
        saturatedFat: {
          grams: {
            value:
              toNullableNumber((resolvedSatFat as any)?.grams?.value)
              ?? toNullableNumber((resolvedSatFat as any)?.value),
            unit: (resolvedSatFat as any)?.grams?.unit || 'g',
          },
          kcal: {
            value: toNullableNumber((resolvedSatFat as any)?.kcal?.value),
            unit: (resolvedSatFat as any)?.kcal?.unit || 'kcal',
          },
        },
        water: {
          value: toNullableNumber((resolvedWater as any)?.value),
          unit: (resolvedWater as any)?.unit || 'ml/kg',
        },
      },
    };

    if (eerOverride !== undefined) {
      updatePayload.eerOverride = eerOverride;
    }

    return {
      updatePayload,
      resolvedProtein,
      resolvedCarbs,
      resolvedFibre,
      resolvedTotalFat,
      resolvedWater,
    };
  };

  const buildEditedClientGoalsFromRecalculation = (newClientGoals: any) => {
    const updatedClientGoals: typeof editedClientGoals = {};

    if (newClientGoals.protein) {
      updatedClientGoals.protein = {
        grams: newClientGoals.protein.grams
          ? {
            value: newClientGoals.protein.grams.value,
            unit: newClientGoals.protein.grams.unit || 'g',
          }
          : undefined,
        kcal: newClientGoals.protein.kcal
          ? {
            value: newClientGoals.protein.kcal.value,
            unit: newClientGoals.protein.kcal.unit || 'kcal',
          }
          : undefined,
      };
    }

    if (newClientGoals.carbohydrates) {
      updatedClientGoals.carbohydrates = {
        energyPercent: newClientGoals.carbohydrates.energyPercent
          ? {
            value: newClientGoals.carbohydrates.energyPercent.value,
            unit: newClientGoals.carbohydrates.energyPercent.unit || '% E',
          }
          : undefined,
        kcal: newClientGoals.carbohydrates.kcal
          ? {
            value: newClientGoals.carbohydrates.kcal.value,
            unit: newClientGoals.carbohydrates.kcal.unit || 'kcal',
          }
          : undefined,
        grams: newClientGoals.carbohydrates.grams
          ? {
            value: newClientGoals.carbohydrates.grams.value,
            unit: newClientGoals.carbohydrates.grams.unit || 'g',
          }
          : undefined,
      };
    }

    if (newClientGoals.dietaryFibre) {
      updatedClientGoals.dietaryFibre = {
        value: newClientGoals.dietaryFibre.value,
        unit: newClientGoals.dietaryFibre.unit || 'g',
      };
    }

    if (newClientGoals.totalFat) {
      updatedClientGoals.totalFat = {
        grams: newClientGoals.totalFat.grams
          ? {
            value: newClientGoals.totalFat.grams.value,
            unit: newClientGoals.totalFat.grams.unit || 'g',
          }
          : undefined,
        kcal: newClientGoals.totalFat.kcal
          ? {
            value: newClientGoals.totalFat.kcal.value,
            unit: newClientGoals.totalFat.kcal.unit || 'kcal',
          }
          : undefined,
      };
    }

    if (newClientGoals.saturatedFat) {
      updatedClientGoals.saturatedFat = {
        grams: newClientGoals.saturatedFat.grams
          ? {
            value: newClientGoals.saturatedFat.grams.value,
            unit: newClientGoals.saturatedFat.grams.unit || 'g',
          }
          : undefined,
        kcal: newClientGoals.saturatedFat.kcal
          ? {
            value: newClientGoals.saturatedFat.kcal.value,
            unit: newClientGoals.saturatedFat.kcal.unit || 'kcal',
          }
          : undefined,
      };
    }

    if (newClientGoals.water) {
      updatedClientGoals.water = {
        value: newClientGoals.water.value,
        unit: newClientGoals.water.unit || 'ml/kg',
      };
    }

    return updatedClientGoals;
  };

  const runAutoRecalculateFromEer = async (eerCalories: number, requestId: number) => {
    if (!client) return;

    try {
      const clientData = {
        action: "calculateMacros",
        age: new Date().getFullYear() - new Date(client.dateOfBirth).getFullYear(),
        eer: eerCalories,
        gender: client.gender,
        location: client.location,
        weightKg: client.weightKg,
        drvSource: client.drvSource || undefined,
      };

      const recalculatedData = await calculationNutritionalTargets(clientData);
      if (requestId !== latestEerRecalculateRequestIdRef.current) return;
      if (!recalculatedData?.clientGoals) return;

      const updatedClientGoals = buildEditedClientGoalsFromRecalculation(recalculatedData.clientGoals);
      const currentClientGoals = client.clientGoals || {};
      const resolvedProtein = updatedClientGoals.protein ?? currentClientGoals.protein;
      const resolvedCarbs = updatedClientGoals.carbohydrates ?? currentClientGoals.carbohydrates;
      const resolvedFibre = updatedClientGoals.dietaryFibre ?? currentClientGoals.dietaryFibre;
      const resolvedTotalFat = updatedClientGoals.totalFat ?? currentClientGoals.totalFat;
      const resolvedWater = updatedClientGoals.water ?? currentClientGoals.water;

      setWarnings(
        recalculatedData.warnings && Array.isArray(recalculatedData.warnings)
          ? recalculatedData.warnings
          : []
      );
      setEditedClientGoals((prev) => ({ ...prev, ...updatedClientGoals }));
      setClientGoal((prev) => ({
        ...prev,
        proteinGoalMin: resolvedProtein?.grams?.value ?? prev.proteinGoalMin ?? '',
        proteinGoalMax: resolvedProtein?.grams?.value ?? prev.proteinGoalMax ?? '',
        carbsGoalMin: resolvedCarbs?.grams?.value ?? prev.carbsGoalMin ?? '',
        carbsGoalMax: resolvedCarbs?.grams?.value ?? prev.carbsGoalMax ?? '',
        fatGoalMin: resolvedTotalFat?.grams?.value ?? prev.fatGoalMin ?? '',
        fatGoalMax: resolvedTotalFat?.grams?.value ?? prev.fatGoalMax ?? '',
        fiberGoalGrams: resolvedFibre?.value ?? prev.fiberGoalGrams ?? '',
        waterGoalLiters: resolvedWater?.value ?? prev.waterGoalLiters ?? '',
      }));
    } catch (error) {
      if (requestId === latestEerRecalculateRequestIdRef.current) {
        console.error('Error auto recalculating macros from EER:', error);
      }
    }
  };

  useEffect(() => {
    if (!isGoalsModalOpen) {
      shouldAutoRecalculateEerRef.current = false;
      clearPendingEerRecalculation();
      return;
    }

    if (!compactView || !shouldAutoRecalculateEerRef.current) return;

    const eerCalories = Number(clientGoal?.eerGoalCalories);
    if (!Number.isFinite(eerCalories) || eerCalories <= 0) return;

    if (eerRecalculateDebounceRef.current) {
      clearTimeout(eerRecalculateDebounceRef.current);
      eerRecalculateDebounceRef.current = null;
    }

    eerRecalculateDebounceRef.current = setTimeout(() => {
      const requestId = latestEerRecalculateRequestIdRef.current + 1;
      latestEerRecalculateRequestIdRef.current = requestId;
      void runAutoRecalculateFromEer(eerCalories, requestId);
    }, 600);

    return () => {
      if (eerRecalculateDebounceRef.current) {
        clearTimeout(eerRecalculateDebounceRef.current);
        eerRecalculateDebounceRef.current = null;
      }
    };
  }, [clientGoal?.eerGoalCalories, compactView, isGoalsModalOpen]);

  const getEditableNutrientInputValue = (
    nutrient: 'protein' | 'carbohydrates' | 'totalFat' | 'dietaryFibre' | 'water' | 'saturatedFat',
    field: 'value' | 'grams' | 'kcal'
  ): number | '' => {
    const hasEditedNutrient = Object.prototype.hasOwnProperty.call(editedClientGoals, nutrient);
    const editedSource = (editedClientGoals as any)[nutrient];
    const originalSource = (client?.clientGoals as any)?.[nutrient];

    if (hasEditedNutrient) {
      if (field === 'value') return editedSource?.value ?? '';
      return editedSource?.[field]?.value ?? '';
    }

    if (field === 'value') return originalSource?.value ?? '';
    return originalSource?.[field]?.value ?? '';
  };

  // Handle preferences data changes
  const handlePreferencesDataChange = (field: string, value: any) => {
    const newValue = value === '' ? null : value;
    setClientGoal(prev => ({ ...prev, [field]: newValue }));
    
    // Track preferences edited fields
    setPreferencesEditedFields(prev => new Set(prev).add(field));
  };

  // Check if macros can be edited (target calories must be set OR clientGoals exist)
  const canEditMacros = () => {
    // Allow editing if legacy calorie goal is set
    if (clientGoal.eerGoalCalories && 
        clientGoal.eerGoalCalories !== '' && 
        !isNaN(Number(clientGoal.eerGoalCalories)) && 
        Number(clientGoal.eerGoalCalories) > 0) {
      return true;
    }
    // Also allow editing if clientGoals data exists (new structure)
    if (client?.clientGoals) {
      return true;
    }
    // Or if client has EER calories
    if (client?.eer || client?.eerOverride) {
      return true;
    }
    return false;
  };

  /** Apply current modal inputs into local state + `editedClientGoals` for later save (no API). */
  const handleRecalculateMacros = () => {
    try {
      const {
        updatePayload,
        resolvedProtein,
        resolvedCarbs,
        resolvedFibre,
        resolvedTotalFat,
        resolvedWater,
      } = buildGoalsUpdatePayload();

      const calorieValidation = getCalorieOverrideValidation();
      if (compactView && calorieValidation.status === 'error') {
        showErrorToast(new Error(calorieValidation.message), 'Validation Error');
        return;
      }
      if (compactView && calorieValidation.status === 'warning') {
        showWarningToast('Low calorie target', calorieValidation.message);
      }

      setWarnings([]);

      const nextClientGoal = {
        ...clientGoal,
        eerGoalCalories:
          updatePayload.eerOverride !== undefined
            ? updatePayload.eerOverride
            : (clientGoal.eerGoalCalories ?? ''),
        proteinGoalMin: resolvedProtein?.grams?.value ?? clientGoal.proteinGoalMin ?? '',
        proteinGoalMax: resolvedProtein?.grams?.value ?? clientGoal.proteinGoalMax ?? '',
        carbsGoalMin: resolvedCarbs?.grams?.value ?? clientGoal.carbsGoalMin ?? '',
        carbsGoalMax: resolvedCarbs?.grams?.value ?? clientGoal.carbsGoalMax ?? '',
        fatGoalMin: resolvedTotalFat?.grams?.value ?? clientGoal.fatGoalMin ?? '',
        fatGoalMax: resolvedTotalFat?.grams?.value ?? clientGoal.fatGoalMax ?? '',
        fiberGoalGrams: resolvedFibre?.value ?? clientGoal.fiberGoalGrams ?? '',
        waterGoalLiters: resolvedWater?.value ?? clientGoal.waterGoalLiters ?? '',
      };

      originalValuesRef.current = { ...nextClientGoal };
      setClientGoal(nextClientGoal);
      setEditedClientGoals(updatePayload.clientGoalsPatch as EditedClientGoalsState);
      setIsEditMode(false);
      setEditMode(null);
      setEditedFields(new Set());
      setIsGoalsModalOpen(false);
      onGoalsSaved?.();
    } catch (error) {
      console.error('Error recalculating macros:', error);
      showErrorToast(error, "Error");
      setWarnings([]);
    }
  };


  // Cuisine options from enum
  const cuisineSuggestions = getValidCuisineTypes().map(cuisine => ({
    id: cuisine.value,
    name: cuisine.name
  }));

  // Allergies options from enum
  const allergySuggestions = getValidAllergies().map(allergy => ({
    id: allergy.value,
    name: allergy.name
  }));

  // Preferences options from enum
  const preferenceSuggestions = getValidPreferences().map(preference => ({
    id: preference.value,
    name: preference.name
  }));

  // Food intolerances options
  const foodIntoleranceSuggestions = getValidFoodIntolerances().map(item => ({
    id: item.value,
    name: item.name
  }));

  const formatEnumLikeLabel = (value: string) =>
    value
      .split(/[_-\s]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');

  // Convert cuisine type array to tags format
  const getCuisineTags = () => {
    if (!clientGoal.cuisineType || clientGoal.cuisineType.length === 0) return [];
    return clientGoal.cuisineType.map(cuisine => ({
      id: cuisine,
      name: cuisineSuggestions.find(s => s.id === cuisine)?.name || cuisine
    }));
  };

  // Handle tag addition
  const handleCuisineAddition = (tag: { id: string; name: string }) => {
    const currentCuisines = clientGoal.cuisineType || [];
    if (!currentCuisines.includes(tag.id)) {
      const updatedCuisines = [...currentCuisines, tag.id];
      handlePreferencesDataChange('cuisineType', updatedCuisines);
    }
  };

  // Handle tag deletion
  const handleCuisineDelete = (i: number) => {
    const currentCuisines = clientGoal.cuisineType || [];
    const updatedCuisines = currentCuisines.filter((_, index) => index !== i);
    handlePreferencesDataChange('cuisineType', updatedCuisines);
  };

  // Convert allergies array to tags format
  const getAllergyTags = () => {
    if (!clientGoal.allergies || clientGoal.allergies.length === 0) return [];
    return clientGoal.allergies.map(allergy => ({
      id: allergy,
      name: allergySuggestions.find(s => s.id === allergy)?.name || formatEnumLikeLabel(allergy)
    }));
  };

  // Handle allergy addition
  const handleAllergyAddition = (allergy: { id: string; name: string }) => {
    const currentAllergies = clientGoal.allergies || [];
    if (!currentAllergies.includes(allergy.id)) {
      const updatedAllergies = [...currentAllergies, allergy.id];
      handlePreferencesDataChange('allergies', updatedAllergies);
    }
  };

  // Handle allergy deletion
  const handleAllergyDelete = (i: number) => {
    const currentAllergies = clientGoal.allergies || [];
    const updatedAllergies = currentAllergies.filter((_, index) => index !== i);
    handlePreferencesDataChange('allergies', updatedAllergies);
  };

  // Convert preferences array to tags format
  const getPreferenceTags = () => {
    if (!clientGoal.preferences || clientGoal.preferences.length === 0) return [];
    return clientGoal.preferences.map(preference => ({
      id: preference,
      name: preferenceSuggestions.find(s => s.id === preference)?.name || preference
    }));
  };

  // Convert food intolerances array to tags format
  const getFoodIntoleranceTags = () => {
    if (!clientGoal.foodIntolerances || clientGoal.foodIntolerances.length === 0) return [];
    return clientGoal.foodIntolerances.map(item => ({
      id: item,
      name: foodIntoleranceSuggestions.find(s => s.id === item)?.name || item
    }));
  };

  // Get total calories from client
  const getDisplayCalorieGoalValue = (): number | null => {
    const localGoal = parseOptionalNumber(clientGoal.eerGoalCalories);
    if (localGoal !== undefined && localGoal > 0) return localGoal;

    const overrideGoal = parseOptionalNumber(client?.eerOverride);
    if (overrideGoal !== undefined && overrideGoal > 0) return overrideGoal;

    const calculatedEer = parseOptionalNumber(client?.eer);
    if (calculatedEer !== undefined && calculatedEer > 0) return calculatedEer;

    return null;
  };

  const getReferenceCalorieValue = (): number | null => {
    const calculatedEer = parseOptionalNumber(client?.eer);
    if (calculatedEer !== undefined && calculatedEer > 0) return calculatedEer;
    return getDisplayCalorieGoalValue();
  };

  const getTotalCalories = (): number => {
    return getDisplayCalorieGoalValue() ?? 0;
  };

  const getCalorieOverrideValidation = () =>
    validateMealPlanCalorieOverride(client, clientGoal.eerGoalCalories);

  const renderCalorieOverrideNotice = (className = '') => {
    const validation = getCalorieOverrideValidation();
    if (validation.status === 'ok') return null;

    const isError = validation.status === 'error';

    return (
      <div
        className={`flex items-start gap-2 rounded-md border px-3 py-2 text-sm ${
          isError
            ? 'border-red-200 bg-red-50 text-red-900'
            : 'border-amber-200 bg-amber-50 text-amber-900'
        } ${className}`}
      >
        <AlertTriangle className={`mt-0.5 h-4 w-4 flex-shrink-0 ${isError ? 'text-red-600' : 'text-amber-600'}`} />
        <p>{validation.message}</p>
      </div>
    );
  };

  const getFormattedCalorieSummary = (): { current: string; target: string } => {
    const currentNum = getDisplayCalorieGoalValue();
    const targetNum = getReferenceCalorieValue();

    return {
      current:
        currentNum !== null
          ? `${Math.round(currentNum).toLocaleString()} kcal/day`
          : 'NA',
      target:
        targetNum !== null
          ? `${Math.round(targetNum).toLocaleString()} kcal/day`
          : 'N/A',
    };
  };

  const getGoalStateNumericValue = (
    goals: EditedClientGoalsState,
    nutrient: 'protein' | 'carbohydrates' | 'totalFat' | 'dietaryFibre',
    field: 'grams' | 'kcal' | 'value'
  ): number | null => {
    const hasEditedNutrient = Object.prototype.hasOwnProperty.call(goals, nutrient);
    const source = hasEditedNutrient
      ? (goals as any)[nutrient]
      : (client?.clientGoals as any)?.[nutrient];

    if (!source) return null;
    if (field === 'value') return toNullableNumber(source?.value);
    return toNullableNumber(source?.[field]?.value ?? (field === 'grams' ? source?.value : null));
  };

  const getMacroSummaryFromGoalState = (goals: EditedClientGoalsState = editedClientGoals) => {
    const proteinInput = getGoalStateNumericValue(goals, 'protein', 'grams');
    const carbsInput = getGoalStateNumericValue(goals, 'carbohydrates', 'grams');
    const fatInput = getGoalStateNumericValue(goals, 'totalFat', 'grams');
    const fibreInput = getGoalStateNumericValue(goals, 'dietaryFibre', 'value');

    const proteinG = Math.max(0, proteinInput ?? 0);
    const carbsG = Math.max(0, carbsInput ?? 0);
    const fatG = Math.max(0, fatInput ?? 0);
    const fibreG = Math.max(0, fibreInput ?? 0);

    const proteinKcal = Math.round(proteinG * MODAL_MACRO_KCAL_PER_GRAM.protein);
    const carbsKcal = Math.round(carbsG * MODAL_MACRO_KCAL_PER_GRAM.carbohydrates);
    const fatKcal = Math.round(fatG * MODAL_MACRO_KCAL_PER_GRAM.totalFat);
    const fibreKcal = Math.round(fibreG * MODAL_MACRO_KCAL_PER_GRAM.dietaryFibre);
    // Protein, carbohydrate, and total fat already allocate 100% of the energy
    // target. Fibre is tracked separately and must not be added a second time.
    const totalKcal = proteinKcal + carbsKcal + fatKcal;
    const hasAnyMacroInput = [proteinInput, carbsInput, fatInput].some((value) => value !== null);

    const pct = (value: number) => {
      return macroEnergyPercentage(value, getDisplayCalorieGoalValue() ?? totalKcal);
    };

    return {
      proteinG,
      carbsG,
      fatG,
      fibreG,
      proteinInput,
      carbsInput,
      fatInput,
      fibreInput,
      proteinKcal,
      carbsKcal,
      fatKcal,
      fibreKcal,
      totalKcal,
      hasAnyMacroInput,
      proteinPct: pct(proteinKcal),
      carbsPct: pct(carbsKcal),
      fatPct: pct(fatKcal),
      fibrePct: pct(fibreKcal),
    };
  };

  const getModalMacroSummary = () => getMacroSummaryFromGoalState();

  const syncClientGoalFromModalMacroValues = (
    goals: EditedClientGoalsState,
    updateCalorieGoal = true,
  ) => {
    if (!compactView) return;

    shouldAutoRecalculateEerRef.current = false;
    clearPendingEerRecalculation();

    const summary = getMacroSummaryFromGoalState(goals);
    const nextCalorieGoal = summary.totalKcal;
    setClientGoal((prev) => ({
      ...prev,
      eerGoalCalories: updateCalorieGoal
        ? (summary.hasAnyMacroInput ? nextCalorieGoal : '')
        : prev.eerGoalCalories,
      proteinGoalMin: summary.proteinInput ?? '',
      proteinGoalMax: summary.proteinInput ?? '',
      carbsGoalMin: summary.carbsInput ?? '',
      carbsGoalMax: summary.carbsInput ?? '',
      fatGoalMin: summary.fatInput ?? '',
      fatGoalMax: summary.fatInput ?? '',
      fiberGoalGrams: summary.fibreInput ?? '',
    }));
  };

  const renderModalMacroHint = (nutrient: MacroGoalNutrient) => (
    <div className="flex items-start gap-2 rounded-md border border-cyan-100 bg-cyan-50 px-3 py-2 text-xs leading-relaxed text-slate-600">
      <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-cyan-600" />
      <span>{getMacroGoalHint(client, nutrient)}</span>
    </div>
  );

  // Get formula from drvSource (primary) or calculationDetails.formulaUsed (fallback)
  // drvSource is the authoritative source from backend API response
  const getFormula = (): string | undefined | null => {
    return client?.drvSource || client?.calculationDetails?.formulaUsed;
  };

  // Check if IOM USA formula is being used
  const isIomUsa = (): boolean => {
    return isIomUsaFormula(getFormula());
  };

  // Check if EFSA EU formula is being used
  const isEfsaEu = (): boolean => {
    return isEfsaEuFormula(getFormula());
  };

  // Check if SACN formula is being used
  const isSacn = (): boolean => {
    return isSacnFormula(getFormula());
  };

  // Check if ICMR India formula is being used
  const isIcmrIndia = (): boolean => {
    return isIcmrIndiaFormula(getFormula());
  };

  // Check if AUS/NZ NRV formula is being used
  const isAusNzNrv = (): boolean => {
    return isAusNzNrvFormula(getFormula());
  };

  // Check if Nordic NNR formula is being used
  const isNordicNnr = (): boolean => {
    return isNordicNnrFormula(getFormula());
  };

  // Helper: do we support traffic lights for the active formula?
  const isTrafficLightSupportedFormula = (): boolean => {
    return isIomUsa() || isEfsaEu() || isSacn() || isIcmrIndia() || isAusNzNrv() || isNordicNnr();
  };

  // Helper: get the formula display name for traffic lights
  const getFormulaDisplayName = (): string => {
    if (isIomUsa()) return 'IOM USA';
    if (isEfsaEu()) return 'EFSA EU';
    if (isSacn()) return 'SACN';
    if (isIcmrIndia()) return 'ICMR India';
    if (isAusNzNrv()) return 'AUS/NZ NRV';
    if (isNordicNnr()) return 'Nordic NNR';
    return '';
  };

  // Evaluate traffic light status for a nutrient
  const evaluateNutrientStatus = (nutrientKey: string): { status: 'red' | 'amber' | 'green' | 'purple' | 'grey'; message: string } | null => {
    // Only evaluate when a supported guideline formula is used
    // Supported formulas: IOM USA, EFSA EU, SACN, ICMR India, AUS/NZ NRV, Nordic NNR
    if (!isTrafficLightSupportedFormula()) {
      return null;
    }

    const nutrientConfig = getNutrientConfig(nutrientKey);
    if (!nutrientConfig) {
      return null;
    }

    // Extract client goal values
    let clientGoalGrams = extractClientGoalGrams(client?.clientGoals, nutrientKey);
    const clientGoalKcal = extractClientGoalKcal(client?.clientGoals, nutrientKey);
    const totalCalories = getTotalCalories();

    // Special handling for water: convert liters to grams (1L = 1000g for water)
    if (nutrientKey === 'water' && clientGoalGrams !== null && clientGoalGrams !== undefined) {
      // Water is stored in liters, but we need grams for evaluation
      // Actually, for water, the reference value is likely in liters too, so we should keep it as-is
      // But let's check the unit - if it's in L, we should convert to match reference
      // For now, assume water values are already in the correct unit (L or g) based on reference
      // The reference extraction will handle the unit conversion
    }

    // Extract AMDR values
    const { amdrLow, amdrHigh } = extractAmdrValues(client?.drvRequirements, nutrientConfig.nutrientId);

    // Extract reference value (RDA/AI, not UL)
    let referenceValue = extractReferenceValue(client?.drvRequirements, nutrientConfig.nutrientId);

    // Extract UL value separately for safety threshold check
    const ulValue = extractUlValue(client?.drvRequirements, nutrientConfig.nutrientId);

    // For water, handle unit conversion if needed
    if (nutrientKey === 'water' && referenceValue !== null) {
      // Water reference might be in L, and client goal might be in L too
      // We'll compare directly assuming same units
      // If reference is in L and goal is in L, no conversion needed
    }

    // Get percentage range for max-only detection
    const percentageRange = getPercentageRange(
      nutrientKey === 'totalFat' ? 'total_fat' 
      : nutrientKey === 'saturatedFat' ? 'saturated_fat' 
      : nutrientKey === 'dietaryFibre' ? 'dietary_fibre'
      : nutrientKey === 'carbohydrates' ? 'carbohydrate'
      : nutrientKey
    );

    // Build evaluation input
    const evaluationInput: EvaluationInput = {
      nutrientKey,
      clientGoalGrams,
      clientGoalKcal,
      totalCalories,
      amdrLow,
      amdrHigh,
      referenceValue,
      ulValue,
      percentageRange,
      nutrientConfig,
    };

    // Evaluate
    const result = evaluateClientGoal(evaluationInput);
    return result;
  };

  // Helper function to get status color classes
  const getStatusColorClasses = (status: 'red' | 'amber' | 'green' | 'purple' | 'grey'): { text: string } => {
    const colors = {
      red: { text: 'text-red-500' },
      amber: { text: 'text-amber-500' },
      green: { text: 'text-green-500' },
      purple: { text: 'text-purple-500' },
      grey: { text: 'text-gray-400' },
    };
    return colors[status];
  };

  // Helper function to render Client Goal with status indicator
  const renderClientGoalWithStatus = (
    nutrientKey: string,
    value: { primary: string; kcal?: string },
    isEditing: boolean = false
  ) => {
    // Only evaluate status if formula supports traffic lights
    // For transFat and freeSugars, also check if IOM USA is used
    const shouldShowStatus = isTrafficLightSupportedFormula() && 
      (nutrientKey !== 'transFat' && nutrientKey !== 'freeSugars' || isIomUsa());
    
    const status = shouldShowStatus ? evaluateNutrientStatus(nutrientKey) : null;
    const statusColor = status ? getStatusColorClasses(status.status) : null;

    const content = (
      <div className="flex items-center gap-1.5">
        <span className={`text-sm ${statusColor?.text || ''} ${!canEditMacros() ? 'opacity-50' : ''}`}>
          {value.primary}
        </span>
        {value.kcal && (
          <span
            className={`text-xs ${
              statusColor?.text || 'text-muted-foreground'
            } ${!canEditMacros() ? 'opacity-50' : ''}`}
          >
            ({value.kcal})
          </span>
        )}
      </div>
    );

    if (status && status.message) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              {content}
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-sm italic">{status.message}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return content;
  };

  // Helper function to get AMDR percentage range from drvRequirements
  // For fat nutrients: check base nutrient first, then _percent_e version's AMDR
  // For non-fat nutrients (protein, carbohydrate): use base nutrient's AMDR
  const getPercentageRange = (nutrientId: string, showULIfNoAMDR: boolean = false): string => {
    const macroSummary = getMacroSummaryFromGoalState();
    const actualEnergyPercentage = {
      protein: macroSummary.proteinPct,
      carbohydrate: macroSummary.carbsPct,
      total_fat: macroSummary.fatPct,
    }[nutrientId];

    // Keep this column aligned with the edit modal: it represents the client's
    // actual macro target, using the displayed daily calorie goal as denominator.
    if (actualEnergyPercentage !== undefined && macroSummary.totalKcal > 0) {
      return `${actualEnergyPercentage.toFixed(1)}%`;
    }

    if (!client?.drvRequirements?.macronutrients) return 'N/A';
    
    const macros = client.drvRequirements.macronutrients;
    const allMacros = [
      ...(macros.proteins || []),
      ...(macros.carbohydrates || []),
      ...(macros.lipids || [])
    ];
    
    // Fat nutrients should check base nutrient first, then _percent_e version
    const fatNutrients = ['total_fat', 'saturated_fat', 'trans_fat', 'free_sugars'];
    const isFatNutrient = fatNutrients.includes(nutrientId);
    
    let nutrient;
    
    if (isFatNutrient && !nutrientId.endsWith('_percent_e')) {
      // For fat nutrients, first try the base nutrient entry
      nutrient = allMacros.find(n => n.nutrientId === nutrientId);
      
      // If base nutrient doesn't have AMDR values, try _percent_e version
      if (!nutrient || (nutrient.amdrLow === null && nutrient.amdrHigh === null)) {
        const percentENutrient = allMacros.find(n => n.nutrientId === `${nutrientId}_percent_e`);
        if (percentENutrient) {
          nutrient = percentENutrient;
        }
      }
    } else {
      // For non-fat nutrients or already _percent_e versions, use direct lookup
      nutrient = allMacros.find(n => n.nutrientId === nutrientId);
    }
    
    if (nutrient) {
      // Check if it has AMDR range
      if (nutrient.amdrLow !== null && nutrient.amdrHigh !== null) {
        return `${nutrient.amdrLow}% - ${nutrient.amdrHigh}%`;
      }
      
      // For fat nutrients: if no AMDR but UL is available, show UL as upper limit
      if (showULIfNoAMDR && nutrient.ul !== null && nutrient.ul !== undefined) {
        return `≤${nutrient.ul}%`;
      }
    }
    
    return 'N/A';
  };

  // Helper function to calculate percentage of calories from macros
  const calculatePercentage = (grams: number | string | null | undefined, caloriesPerGram: number): string => {
    const totalCalories = getTotalCalories();
    if (!grams || grams === '' || isNaN(Number(grams)) || totalCalories === 0) return 'N/A';
    const gramsNum = Number(grams);
    const calories = gramsNum * caloriesPerGram;
    const percentage = (calories / totalCalories) * 100;
    return `${percentage.toFixed(1)}%`;
  };

  // Helper function to calculate percentage range from min/max
  const calculatePercentageRange = (
    min: number | string | null | undefined,
    max: number | string | null | undefined,
    caloriesPerGram: number
  ): string => {
    const totalCalories = getTotalCalories();
    if (!min || !max || min === '' || max === '' || isNaN(Number(min)) || isNaN(Number(max)) || totalCalories === 0) {
      return 'N/A';
    }
    const minGrams = Number(min);
    const maxGrams = Number(max);
    const minCalories = minGrams * caloriesPerGram;
    const maxCalories = maxGrams * caloriesPerGram;
    const minPercentage = (minCalories / totalCalories) * 100;
    const maxPercentage = (maxCalories / totalCalories) * 100;
    return `${minPercentage.toFixed(1)}% - ${maxPercentage.toFixed(1)}%`;
  };

  const formatRangeValue = (value: number): string =>
    Number.isInteger(value) ? String(value) : String(Number(value.toFixed(1)));

  const formatMacroGramUnit = (unit?: string | null): string => {
    const normalized = String(unit || '').trim().toLowerCase();
    if (normalized.includes('g') && normalized.includes('day')) return unit || 'g/day';
    return 'g/day';
  };

  const getBackendMacroGramRange = (nutrient: {
    amdrGrams?: number | null;
    amdrLowGrams?: number | null;
    amdrHighGrams?: number | null;
    ulGrams?: number | null;
    aiGrams?: number | null;
    unit?: string | null;
  }): string | null => {
    const unit = formatMacroGramUnit(nutrient.unit);

    if (Number.isFinite(nutrient.amdrLowGrams) && Number.isFinite(nutrient.amdrHighGrams)) {
      return `${formatRangeValue(nutrient.amdrLowGrams as number)} - ${formatRangeValue(nutrient.amdrHighGrams as number)} ${unit}`;
    }

    if (Number.isFinite(nutrient.amdrGrams)) {
      return `${formatRangeValue(nutrient.amdrGrams as number)} ${unit}`;
    }

    if (Number.isFinite(nutrient.ulGrams)) {
      return `≤${formatRangeValue(nutrient.ulGrams as number)} ${unit}`;
    }

    if (Number.isFinite(nutrient.aiGrams)) {
      return `${formatRangeValue(nutrient.aiGrams as number)} ${unit}`;
    }

    return null;
  };

  // Helper to get reference range from drvRequirements
  // Priority:
  // 1) RDA
  // 2) Backend-calculated macro gram values from DRV %E × EER
  // 3) AI → UL (UI fallback)
  const getReferenceRange = (nutrientId: string): string => {
    const key = ({ protein: 'protein', carbohydrate: 'carbs', total_fat: 'fat', saturated_fat: 'saturatedFat', dietary_fibre: 'fibre', free_sugars: 'freeSugars', sodium: 'sodium' } as Record<string, string>)[nutrientId];
    const limit = (client?.clientGoals as any)?.clinicalRules?.resolvedTargets?.limits?.[key];
    const clinicalRange = formatStructuredClinicalLimit(limit);
    if (clinicalRange) return clinicalRange;
    if (!client?.drvRequirements) return 'N/A';
    
    const drv = client.drvRequirements;
    
    // Search in macronutrients (proteins, carbohydrates, lipids)
    if (drv.macronutrients) {
      const macros = drv.macronutrients;
      const allMacros = [
        ...(macros.proteins || []),
        ...(macros.carbohydrates || []),
        ...(macros.lipids || [])
      ];
      
      const nutrient = allMacros.find(n => n.nutrientId === nutrientId);
      if (nutrient) {
        // Priority: RDA first
        if (nutrient.rda !== null) {
          return `${nutrient.rda}${nutrient.unit}`;
        }

        const backendMacroGramRange = getBackendMacroGramRange(nutrient);
        if (backendMacroGramRange) return backendMacroGramRange;

        // UI fallback: AI
        if (nutrient.ai !== null) {
          return `${nutrient.ai}${nutrient.unit}`;
        }
        // UI fallback: UL
        if (nutrient.ul !== null) {
          return `${nutrient.ul}${nutrient.unit}`;
        }
      }
    }
    
    // Search in 'other' category for water
    if (nutrientId === 'water' && drv.other) {
      const waterNutrient = drv.other.find(n => n.nutrientId === 'water');
      if (waterNutrient) {
        // Priority: RDA first
        if (waterNutrient.rda !== null) {
          return `${waterNutrient.rda}${waterNutrient.unit}`;
        }
        // UI fallback: AI
        if (waterNutrient.ai !== null) {
          return `${waterNutrient.ai}${waterNutrient.unit}`;
        }
        // UI fallback: UL
        if (waterNutrient.ul !== null) {
          return `${waterNutrient.ul}${waterNutrient.unit}`;
        }
      }
    }
    
    return 'N/A';
  };

  // Helper to get kcal value for a nutrient from backend (no calculations, only read from backend)
  const getKcalValue = (nutrient: 'protein' | 'carbohydrates' | 'totalFat' | 'saturatedFat' | 'dietaryFibre' | 'water'): string | null => {
    if (!client?.clientGoals) return null;
    
    // Carbohydrates has nested kcal field
    if (nutrient === 'carbohydrates') {
      const kcal = client.clientGoals.carbohydrates?.kcal?.value;
      if (kcal !== undefined && kcal !== null) {
        return `${kcal} ${client.clientGoals.carbohydrates?.kcal?.unit || 'kcal'}`;
      }
    }
    
    // Protein has nested kcal field
    if (nutrient === 'protein' && client.clientGoals.protein?.kcal) {
      const kcal = client.clientGoals.protein.kcal.value;
      if (kcal !== undefined && kcal !== null) {
        return `${kcal} ${client.clientGoals.protein.kcal.unit || 'kcal'}`;
      }
    }
    
    // Total fat has nested kcal field
    if (nutrient === 'totalFat' && client.clientGoals.totalFat?.kcal) {
      const kcal = client.clientGoals.totalFat.kcal.value;
      if (kcal !== undefined && kcal !== null) {
        return `${kcal} ${client.clientGoals.totalFat.kcal.unit || 'kcal'}`;
      }
    }
    
    // Saturated fat has nested kcal field
    if (nutrient === 'saturatedFat' && client.clientGoals.saturatedFat?.kcal) {
      const kcal = client.clientGoals.saturatedFat.kcal.value;
      if (kcal !== undefined && kcal !== null) {
        return `${kcal} ${client.clientGoals.saturatedFat.kcal.unit || 'kcal'}`;
      }
    }
    
    // dietaryFibre and water don't have kcal in the API structure
    return null;
  };


  // Helper to get client goal value from clientGoals (no calculations, only display backend values)
  // Checks editedClientGoals first (for recalculated values), then uses client.clientGoals.
  const getClientGoalValue = (nutrient: 'protein' | 'carbohydrates' | 'totalFat' | 'saturatedFat' | 'dietaryFibre' | 'water'): { primary: string; kcal?: string } => {
    if (nutrient === 'protein') {
      // Protein can have either direct structure (value/unit) or nested structure (grams/kcal)
      // Check editedClientGoals first (only when the nutrient was edited), then client.clientGoals.
      const proteinSource = Object.prototype.hasOwnProperty.call(editedClientGoals, 'protein')
        ? editedClientGoals.protein
        : client?.clientGoals?.protein;
      const gramsValue =
        proteinSource?.grams?.value ??
        (proteinSource as any)?.value;
      const gramsUnit = proteinSource?.grams?.unit || (proteinSource as any)?.unit || 'g';
      const primary = gramsValue !== undefined && gramsValue !== null ? `${gramsValue}${gramsUnit}` : 'Not set';
      const kcalValue = proteinSource?.kcal?.value;
      const kcalUnit = proteinSource?.kcal?.unit || 'kcal';
      const kcal = kcalValue !== undefined && kcalValue !== null ? `${kcalValue} ${kcalUnit}` : undefined;
      return { primary, kcal };
    }
    
    if (nutrient === 'carbohydrates') {
      const carbsSource = Object.prototype.hasOwnProperty.call(editedClientGoals, 'carbohydrates')
        ? editedClientGoals.carbohydrates
        : client?.clientGoals?.carbohydrates;
      const grams = carbsSource?.grams?.value;
      const unit = carbsSource?.grams?.unit || 'g';
      const primary = grams !== undefined && grams !== null ? `${grams}${unit}` : 'Not set';
      const kcalValue = carbsSource?.kcal?.value;
      const kcalUnit = carbsSource?.kcal?.unit || 'kcal';
      const kcal = kcalValue !== undefined && kcalValue !== null ? `${kcalValue} ${kcalUnit}` : undefined;
      return { primary, kcal };
    }
    
    if (nutrient === 'totalFat') {
      const fatSource = Object.prototype.hasOwnProperty.call(editedClientGoals, 'totalFat')
        ? editedClientGoals.totalFat
        : client?.clientGoals?.totalFat;
      const grams =
        fatSource?.grams?.value ??
        (fatSource as any)?.value;
      const gramsUnit = fatSource?.grams?.unit || (fatSource as any)?.unit || 'g';
      const primary = grams !== undefined && grams !== null ? `${grams}${gramsUnit}` : 'Not set';
      const kcalValue = fatSource?.kcal?.value;
      const kcalUnit = fatSource?.kcal?.unit || 'kcal';
      const kcal = kcalValue !== undefined && kcalValue !== null ? `${kcalValue} ${kcalUnit}` : undefined;
      return { primary, kcal };
    }
    
    if (nutrient === 'saturatedFat') {
      const satFatSource = Object.prototype.hasOwnProperty.call(editedClientGoals, 'saturatedFat')
        ? editedClientGoals.saturatedFat
        : client?.clientGoals?.saturatedFat;
      const grams = satFatSource?.grams?.value ?? (satFatSource as any)?.value;
      const gramsUnit = satFatSource?.grams?.unit || (satFatSource as any)?.unit || 'g';
      const primary = grams !== undefined && grams !== null ? `${grams}${gramsUnit}` : 'Not set';
      const kcalValue = satFatSource?.kcal?.value;
      const kcalUnit = satFatSource?.kcal?.unit || 'kcal';
      const kcal = kcalValue !== undefined && kcalValue !== null ? `${kcalValue} ${kcalUnit}` : undefined;
      return { primary, kcal };
    }
    
    if (nutrient === 'dietaryFibre') {
      const fiberSource = Object.prototype.hasOwnProperty.call(editedClientGoals, 'dietaryFibre')
        ? editedClientGoals.dietaryFibre
        : client?.clientGoals?.dietaryFibre;
      const value = fiberSource?.value;
      const unit = fiberSource?.unit || 'g';
      const primary = value !== undefined && value !== null ? `${value}${unit}` : 'Not set';
      return { primary };
    }
    
    if (nutrient === 'water') {
      const waterSource = Object.prototype.hasOwnProperty.call(editedClientGoals, 'water')
        ? editedClientGoals.water
        : client?.clientGoals?.water;
      const value = waterSource?.value;
      const unit = waterSource?.unit || 'L';
      const primary = value !== undefined && value !== null ? `${value}${unit}` : 'Not set';
      return { primary };
    }
    
    return { primary: 'Not set' };
  };

  // Helper to get trans fat value (read-only)
  const getTransFatValue = (): { primary: string; kcal?: string } => {
    if (!client?.clientGoals?.transFat) {
      return { primary: 'Not set' };
    }
    const value = client.clientGoals.transFat.grams?.value;
    const unit = client.clientGoals.transFat.grams?.unit || 'g';
    const primary = value !== undefined && value !== null ? `${value}${unit}` : 'Not set';
    const kcal = client.clientGoals.transFat.kcal?.value;
    const kcalStr = kcal !== undefined && kcal !== null ? `${kcal} ${client.clientGoals.transFat.kcal?.unit || 'kcal'}` : undefined;
    return { primary, kcal: kcalStr };
  };

  // Helper to get free sugars value (read-only)
  const getFreeSugarsValue = (): string => {
    if (!client?.clientGoals?.freeSugars) {
      return 'Not set';
    }
    const value = client.clientGoals.freeSugars.value;
    const unit = client.clientGoals.freeSugars.unit || 'g';
    return value !== undefined && value !== null ? `${value}${unit}` : 'Not set';
  };

  // Handle editing clientGoals values
  const handleEditClientGoal = (nutrient: string) => {
    setEditingClientGoal(nutrient);
    // Always initialize editedClientGoals with current values from client
    if (client?.clientGoals) {
      const currentValue = client.clientGoals[nutrient as keyof typeof client.clientGoals];
      if (currentValue) {
        // Deep copy to avoid reference issues
        const valueToCopy = nutrient === 'carbohydrates' 
          ? JSON.parse(JSON.stringify(currentValue))
          : { ...(currentValue as any) };
        setEditedClientGoals(prev => ({
          ...prev,
          [nutrient]: valueToCopy
        }));
      }
    }
  };

  // Handle clientGoal value change - auto-updates linked field (grams ↔ kcal)
  const handleClientGoalChange = (nutrient: string, field: string, value: number | null) => {
    if (value !== null && (!Number.isFinite(value) || value < 0)) return; // reject invalid, negative

    const buildNextEditedClientGoals = (prev: EditedClientGoalsState): EditedClientGoalsState => {
      const current = prev[nutrient as keyof typeof prev] ||
        (client?.clientGoals?.[nutrient as keyof typeof client.clientGoals] as any) || {};

      if (nutrient === 'carbohydrates') {
        const carbsValue = current as any;
        let grams = carbsValue?.grams?.value;
        let kcal = carbsValue?.kcal?.value;
        if (field === 'grams') {
          grams = value;
          kcal = value === null ? null : Math.round(value * 4);
        } else if (field === 'kcal') {
          kcal = value;
          grams = value === null ? null : Math.round((value / 4) * 10) / 10;
        } else {
          return {
            ...prev,
            carbohydrates: { ...carbsValue, [field]: { value, unit: field === 'kcal' ? 'kcal' : field === 'grams' ? 'g' : '% E' } }
          };
        }
        return {
          ...prev,
          carbohydrates: {
            ...carbsValue,
            grams: { value: grams, unit: 'g' },
            kcal: { value: kcal, unit: 'kcal' },
          }
        };
      }

      if (nutrient === 'protein' || nutrient === 'totalFat' || nutrient === 'saturatedFat') {
        const currentNutrient = current as any;
        const isFat = nutrient === 'totalFat' || nutrient === 'saturatedFat';
        const kcalPerGram = isFat ? 9 : 4;
        let grams = currentNutrient?.grams?.value;
        let kcal = currentNutrient?.kcal?.value;
        if (field === 'grams') {
          grams = value;
          kcal = value === null ? null : Math.round(value * kcalPerGram);
        } else if (field === 'kcal') {
          kcal = value;
          grams = value === null ? null : Math.round((value / kcalPerGram) * 10) / 10;
        }
        return {
          ...prev,
          [nutrient]: {
            ...currentNutrient,
            grams: { value: grams, unit: 'g' },
            kcal: { value: kcal, unit: 'kcal' },
          }
        };
      }

      return {
        ...prev,
        [nutrient]: {
          ...current,
          value,
          unit: (current as any)?.unit || (nutrient === 'water' ? 'ml/kg' : 'g')
        }
      };
    };

    const nextGoals = buildNextEditedClientGoals(editedClientGoals);
    setEditedClientGoals(nextGoals);

    if (['protein', 'carbohydrates', 'totalFat', 'dietaryFibre'].includes(nutrient)) {
      syncClientGoalFromModalMacroValues(nextGoals, nutrient !== 'dietaryFibre');
    }
  };

  // Handle save clientGoal
  const handleSaveClientGoal = async (nutrient: string) => {
    try {
      const editedValue = editedClientGoals[nutrient as keyof typeof editedClientGoals];
      if (!editedValue) {
        setEditingClientGoal(null);
        return;
      }

      await updateClient(clientId, {
        clientGoalsPatch: {
          [nutrient]: editedValue
        }
      });

      // Close edit mode
      setEditingClientGoal(null);
      setEditedClientGoals({});

      // Refresh client data if available
      if (refreshClientData) {
        await refreshClientData();
      }
      showSuccessToast("Success", "Client goal updated successfully");
    } catch (error) {
      console.error('Error saving client goal:', error);
      showErrorToast(error, "Error");
    }
  };

  // Handle cancel clientGoal edit
  const handleCancelClientGoal = () => {
    setEditingClientGoal(null);
    setEditedClientGoals({});
  };

  // Handle preference addition
  const handlePreferenceAddition = (preference: { id: string; name: string }) => {
    const currentPreferences = clientGoal.preferences || [];
    if (!currentPreferences.includes(preference.id)) {
      const updatedPreferences = [...currentPreferences, preference.id];
      handlePreferencesDataChange('preferences', updatedPreferences);
    }
  };

  // Handle preference deletion
  const handlePreferenceDelete = (i: number) => {
    const currentPreferences = clientGoal.preferences || [];
    const updatedPreferences = currentPreferences.filter((_, index) => index !== i);
    handlePreferencesDataChange('preferences', updatedPreferences);
  };

  const addFoodIntolerance = (item: { id: string; name: string }) => {
    const currentIntolerances = clientGoal.foodIntolerances || [];
    if (!currentIntolerances.includes(item.id)) {
      const updated = [...currentIntolerances, item.id];
      handlePreferencesDataChange('foodIntolerances', updated);
    }
  };

  const findSuggestionByInput = (inputValue: string, options: Option[]): Option | null => {
    const needle = inputValue.trim().toLowerCase();
    if (!needle) return null;

    const exactMatch =
      options.find((option) => option.name.toLowerCase() === needle) ||
      options.find((option) => option.id.toLowerCase() === needle);
    if (exactMatch) return exactMatch;

    return options.find(
      (option) =>
        option.name.toLowerCase().includes(needle) || option.id.toLowerCase().includes(needle)
    ) || null;
  };

  const getVisibleSuggestions = (
    options: Option[],
    selectedValues: string[] | undefined,
    inputValue: string
  ): Option[] => {
    const selectedSet = new Set((selectedValues || []).map((value) => value.toLowerCase()));
    const unselectedOptions = options.filter((option) => !selectedSet.has(option.id.toLowerCase()));
    const needle = inputValue.trim().toLowerCase();

    if (!needle) {
      return unselectedOptions;
    }

    return unselectedOptions.filter(
      (option) =>
        option.name.toLowerCase().includes(needle) || option.id.toLowerCase().includes(needle)
    );
  };

  const addCuisineFromModalInput = () => {
    const match = findSuggestionByInput(cuisineModalInput, cuisineSuggestions);
    if (match) {
      handleCuisineAddition(match);
      setCuisineModalInput('');
    }
  };

  const addAllergyFromModalInput = () => {
    const match = findSuggestionByInput(allergyModalInput, allergySuggestions);
    if (match) {
      handleAllergyAddition(match);
      setAllergyModalInput('');
    }
  };

  const addPreferenceFromModalInput = () => {
    const match = findSuggestionByInput(preferenceModalInput, preferenceSuggestions);
    if (match) {
      handlePreferenceAddition(match);
      setPreferenceModalInput('');
    }
  };

  const addIntoleranceFromModalInput = () => {
    const match = findSuggestionByInput(intoleranceModalInput, foodIntoleranceSuggestions);
    if (match) {
      addFoodIntolerance(match);
      setIntoleranceModalInput('');
    }
  };

  // Enter edit mode for calories + macros together
  const handleEditGoals = () => {
    if (editMode === 'goals') return;

    // Store current values as original
    originalValuesRef.current = { ...clientGoal };
    originalEditedClientGoalsRef.current = deepCopyGoalValue(editedClientGoals) || {};
    setIsEditMode(true);
    setEditMode('goals');

    // Clear any previously edited fields when entering edit mode
    setEditedFields(new Set());

    // Initialize from the latest visible values. Recalculated macros live in
    // editedClientGoals locally, while client.clientGoals may still be the
    // older server snapshot until a refresh lands.
    const fallbackGoals = client?.clientGoals;
    const goals = {
      protein: editedClientGoals.protein ?? fallbackGoals?.protein,
      carbohydrates: editedClientGoals.carbohydrates ?? fallbackGoals?.carbohydrates,
      dietaryFibre: editedClientGoals.dietaryFibre ?? fallbackGoals?.dietaryFibre,
      totalFat: editedClientGoals.totalFat ?? fallbackGoals?.totalFat,
      saturatedFat: editedClientGoals.saturatedFat ?? fallbackGoals?.saturatedFat,
      water: editedClientGoals.water ?? fallbackGoals?.water,
    };

    const nextEditedClientGoals: EditedClientGoalsState = {
      protein: goals.protein ? deepCopyGoalValue(goals.protein) : undefined,
      carbohydrates: goals.carbohydrates ? deepCopyGoalValue(goals.carbohydrates) : undefined,
      dietaryFibre: goals.dietaryFibre ? deepCopyGoalValue(goals.dietaryFibre) : undefined,
      totalFat: goals.totalFat ? deepCopyGoalValue(goals.totalFat) : undefined,
      saturatedFat: goals.saturatedFat ? deepCopyGoalValue(goals.saturatedFat) : undefined,
      water: goals.water ? deepCopyGoalValue(goals.water) : undefined,
    };

    setEditedClientGoals(nextEditedClientGoals);
    originalMacroCalorieGoalRef.current = getMacroSummaryFromGoalState(nextEditedClientGoals).totalKcal;

    // Auto-expand when entering edit mode
    setIsExpanded(true);
  };

  // Cancel edit mode
  const handleCancel = () => {
    // Reset to original values
    setClientGoal(originalValuesRef.current);
    setIsEditMode(false);
    setEditMode(null);
    setEditedFields(new Set());
    setEditedClientGoals(deepCopyGoalValue(originalEditedClientGoalsRef.current) || {});
    setWarnings([]);
  };

  const handleDoneEditingGoals = () => {
    setIsEditMode(false);
    setEditMode(null);
    setWarnings([]);
  };

  const openGoalsEditorModal = () => {
    onGoalsEditOpen?.();
    handleEditGoals();
    setIsGoalsModalOpen(true);
  };

  const closeGoalsEditorModal = () => {
    if (isSaving) return;
    handleCancel();
    setIsGoalsModalOpen(false);
  };

  // Cancel preferences edit mode
  const handlePreferencesCancel = () => {
    // Reset to original values
    setClientGoal(originalValuesRef.current);
    setIsPreferencesEditMode(false);
    setPreferencesEditedFields(new Set());
    setCuisineModalInput('');
    setAllergyModalInput('');
    setIntoleranceModalInput('');
    setWarnings([]);
    // Note: No select values to reset with MultiSelect component
  };

  // Save dietary goals
  const handleSave = async (): Promise<boolean> => {
    try {
      setIsSaving(true);
      
      // Handle calories and macros mode - existing logic
      // Check if Daily Calorie Goals differs by more than 5% from EER calories
      if (clientGoal.eerGoalCalories && client?.eer) {
        const eerCalories = Number(client.eer);
        const targetCalories = Number(clientGoal.eerGoalCalories);
        const difference = Math.abs(targetCalories - eerCalories);
        const percentageDifference = eerCalories && !isNaN(eerCalories) ? (difference / eerCalories) * 100 : 0;
        
        if (percentageDifference > 5) {
          const confirmed = window.confirm(
            `Warning: Your Daily Calorie Goals (${targetCalories} kcal) differs by ${Number(percentageDifference).toFixed(1)}% from the calculated EER (${eerCalories} kcal). This significant difference may not align with your nutritional goals. Do you want to proceed?`
          );
          
          if (!confirmed) {
            setIsSaving(false);
            return false;
          }
        }
      }
      
      const {
        updatePayload,
        resolvedProtein,
        resolvedCarbs,
        resolvedFibre,
        resolvedTotalFat,
        resolvedWater,
      } = buildGoalsUpdatePayload();

      if (!hideSaveButtons) {
        const updateResponse = await updateClient(clientId, updatePayload);
        
        // Check for warnings in update response
        if (updateResponse?.warnings && Array.isArray(updateResponse.warnings) && updateResponse.warnings.length > 0) {
          setWarnings(updateResponse.warnings);
        } else {
          setWarnings([]);
        }
      } else {
        setWarnings([]);
      }
      
      // Common success handling for both modes
      // Store editMode before clearing it for success message
      const currentEditMode = editMode;

      const nextClientGoal = {
        ...clientGoal,
        eerGoalCalories:
          updatePayload.eerOverride !== undefined
            ? updatePayload.eerOverride
            : (clientGoal.eerGoalCalories ?? ''),
        proteinGoalMin: resolvedProtein?.grams?.value ?? clientGoal.proteinGoalMin ?? '',
        proteinGoalMax: resolvedProtein?.grams?.value ?? clientGoal.proteinGoalMax ?? '',
        carbsGoalMin: resolvedCarbs?.grams?.value ?? clientGoal.carbsGoalMin ?? '',
        carbsGoalMax: resolvedCarbs?.grams?.value ?? clientGoal.carbsGoalMax ?? '',
        fatGoalMin: resolvedTotalFat?.grams?.value ?? clientGoal.fatGoalMin ?? '',
        fatGoalMax: resolvedTotalFat?.grams?.value ?? clientGoal.fatGoalMax ?? '',
        fiberGoalGrams: resolvedFibre?.value ?? clientGoal.fiberGoalGrams ?? '',
        waterGoalLiters: resolvedWater?.value ?? clientGoal.waterGoalLiters ?? '',
      };
      
      // Update original values
      originalValuesRef.current = { ...nextClientGoal };
      setClientGoal(nextClientGoal);
      setIsEditMode(false);
      setEditMode(null);
      setEditedFields(new Set());
      // Keep latest saved values locally so compact view reflects changes immediately
      // even when parent does not refresh client details.
      setEditedClientGoals(updatePayload.clientGoalsPatch);
      
      // Refresh client data
      if (!hideSaveButtons && refreshClientData) {
        await refreshClientData();
      }
      
      const successMessage = currentEditMode === 'goals' ? "Calories and macros updated successfully" : "Goals updated successfully";
      showSuccessToast("Success", successMessage);
      return true;
    } catch (error) {
      console.error('Error saving dietary goals:', error);
      showErrorToast(error, "Error");
      setWarnings([]);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Save dietary preferences
  const handlePreferencesSave = async (): Promise<boolean> => {
    try {
      setIsPreferencesSaving(true);
      
      const preferencesPayload: any = {};
      
      // Only include fields that were edited
      // Map to the new API format: allergies, dietaryPreferences, cuisineTypesPreferred
      if (preferencesEditedFields.has('cuisineType')) {
        preferencesPayload.cuisineTypesPreferred = clientGoal.cuisineType || [];
      }
      if (preferencesEditedFields.has('allergies')) {
        preferencesPayload.allergies = clientGoal.allergies || [];
      }
      if (preferencesEditedFields.has('preferences')) {
        preferencesPayload.dietaryPreferences = clientGoal.preferences || [];
      }
      if (preferencesEditedFields.has('foodIntolerances')) {
        preferencesPayload.intolerances = toFoodIntoleranceApiLabels(clientGoal.foodIntolerances);
      }

      if (hideSaveButtons) {
        // In create/generate flows we keep preference edits local and pass them in the generation payload.
        originalValuesRef.current = { ...clientGoal };
        setIsPreferencesEditMode(false);
        setPreferencesEditedFields(new Set());
        setWarnings([]);
        onPreferencesSaved?.();
        return true;
      }
      
      // Debug: Log the payload being sent
      console.log('Sending dietary preferences to API:', preferencesPayload);
      
      // Make API call using the updateClient function (PUT /clients/{clientId})
      const updateResponse = await updateClient(clientId, preferencesPayload);
      
      // Check for warnings in update response
      if (updateResponse?.warnings && Array.isArray(updateResponse.warnings) && updateResponse.warnings.length > 0) {
        setWarnings(updateResponse.warnings);
      } else {
        setWarnings([]);
      }
      
      // Update original values
      originalValuesRef.current = { ...clientGoal };
      setIsPreferencesEditMode(false);
      setPreferencesEditedFields(new Set());
      // Note: No select values to reset with MultiSelect component
      
      // Refresh client data
      if (refreshClientData) {
        await refreshClientData();
      }
      
      showSuccessToast("Success", "Dietary preferences updated successfully");
      onPreferencesSaved?.();
      return true;
    } catch (error) {
      console.error('Error saving dietary preferences:', error);
      showErrorToast(error, "Error");
      setWarnings([]);
      return false;
    } finally {
      setIsPreferencesSaving(false);
    }
  };

  const openPreferencesModal = () => {
    onPreferencesEditOpen?.();
    originalValuesRef.current = { ...clientGoal };
    setPreferencesEditedFields(new Set());
    setIsPreferencesEditMode(true);
    setCuisineModalInput('');
    setAllergyModalInput('');
    setPreferenceModalInput('');
    setIntoleranceModalInput('');
    setIsPreferencesModalOpen(true);
  };

  const closePreferencesModal = () => {
    if (isPreferencesSaving) return;
    handlePreferencesCancel();
    setIsPreferencesModalOpen(false);
  };

  const savePreferencesFromModal = async () => {
    const saved = await handlePreferencesSave();
    if (saved) {
      setCuisineModalInput('');
      setAllergyModalInput('');
      setPreferenceModalInput('');
      setIntoleranceModalInput('');
      setIsPreferencesModalOpen(false);
    }
  };

  // Implement the imperative handle functions
  useImperativeHandle(ref, () => ({
    saveDietaryGoals: async () => {
      await handleSave();
    },
    getCurrentValues: () => {
      return {
        ...clientGoal,
        // Map the correct property names from clientGoal to expected names
        targetCalories: clientGoal.eerGoalCalories,
        proteinMin: clientGoal.proteinGoalMin,
        proteinMax: clientGoal.proteinGoalMax,
        carbsMin: clientGoal.carbsGoalMin,
        carbsMax: clientGoal.carbsGoalMax,
        fatMin: clientGoal.fatGoalMin,
        fatMax: clientGoal.fatGoalMax,
        fiberGrams: clientGoal.fiberGoalGrams,
        waterLiters: clientGoal.waterGoalLiters,
        // Include preferences
        cuisinePreferences: clientGoal.cuisineType || [],
        allergies: clientGoal.allergies || [],
        dietaryPreferences: clientGoal.preferences || [],
        foodIntolerances: clientGoal.foodIntolerances || []
      };
    },
    resetToOriginalValues: () => {
      handleCancel();
    }
  }), [clientGoal]);

  const showInlinePreferencesEditor = isPreferencesEditMode && !compactView;

  return (
    <div className="space-y-6">
    <Card
      className={
        compactView
          ? 'mb-6 border border-slate-200 bg-white shadow-sm'
          : 'mb-8 border-0 bg-gradient-to-br from-background to-green/5 shadow-lg transition-all duration-300 hover:shadow-xl'
      }
    >
      <CardHeader className={compactView ? 'pb-3' : 'pb-4'}>
        <div className="flex items-center justify-between">
          <CardTitle
            className={
              compactView
                ? 'flex items-center gap-2 text-[18px] font-semibold text-slate-800'
                : 'flex items-center text-xl font-bold text-foreground'
            }
          >
            {compactView ? (
              <>
                <Flag className="h-5 w-5 text-cyan-500" />
                Client Goals
              </>
            ) : (
              <div className="flex items-center">
                <div className="mr-3 flex h-10 w-10 items-center justify-center rounded-full bg-green/10">
                  <Target className="h-5 w-5 text-green" />
                </div>
                <div>
                  Client Goals
                  {isEditMode && (
                    <span className="ml-2 text-sm font-normal text-muted-foreground">
                      - Editing Calories and Macros
                    </span>
                  )}
                </div>
              </div>
            )}
          </CardTitle>

          {!compactView && (
            <div className="flex items-center gap-2">
              {!isEditMode ? (
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    onClick={handleEditGoals}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                    data-demo-id="ai-plan-edit-calories"
                  >
                    <Edit className="h-4 w-4" />
                    Edit Calories and Macros
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setIsExpanded(!isExpanded)}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                    {isExpanded ? 'Collapse' : 'Expand'}
                  </Button>
                </div>
              ) : (
                <>
                  {!hideSaveButtons && (
                    <div className="flex items-center gap-2 border-l border-border/50 pl-3">
                      <Button
                        type="button"
                        onClick={handleCancel}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2"
                      >
                        <X className="h-4 w-4" />
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        onClick={handleSave}
                        disabled={isSaving}
                        size="sm"
                        className="flex items-center gap-2"
                      >
                        {isSaving ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4" />
                        )}
                        {isSaving ? 'Saving...' : 'Save'}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {isExpanded && (
        <div className="space-y-6">
            {/* Warnings Display */}
            {warnings.length > 0 && (
              <Alert variant="destructive" className="border-amber-500/60 bg-amber-50 text-amber-900 dark:border-amber-400/60 dark:bg-amber-900/30 dark:text-amber-50">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle className="text-amber-900 dark:text-amber-50">Warnings</AlertTitle>
                <AlertDescription className="text-amber-800 dark:text-amber-100">
                  <ul className="list-disc list-inside space-y-1 mt-2">
                    {warnings.map((warning, index) => (
                      <li key={index}>
                        {warning.message}
                        {warning.minimumRequired && warning.actualValue !== undefined && (
                          <span className="block text-xs mt-1 ml-4">
                            Required: {warning.minimumRequired}, Actual: {warning.actualValue}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}
            {/* Daily Calorie Goals */}
            <div className={compactView ? 'rounded-lg border border-slate-100 bg-slate-50 p-4' : 'rounded-lg border border-border/50 bg-muted/30 p-4'}>
              {compactView ? (
                <div className="space-y-3">
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div>
                      <h4 className="text-[23px] font-semibold leading-none text-slate-800">
                        {getFormattedCalorieSummary().current}
                      </h4>
                      <p className="mt-2 text-xs text-slate-500">
                        Daily Calorie Goal (tar {getFormattedCalorieSummary().target})
                      </p>
                    </div>

                    <div className="flex items-center gap-4">
                      <Button
                        type="button"
                        onClick={openGoalsEditorModal}
                        className="rounded-lg bg-cyan-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-cyan-600"
                        data-analytics-ignore={onGoalsEditOpen ? 'true' : undefined}
                        data-demo-id="ai-plan-edit-calories"
                      >
                        Edit Calories and Macros
                      </Button>
                    </div>
                  </div>
                  {renderCalorieOverrideNotice()}
                </div>
              ) : (
                <div className="grid grid-cols-1 items-center gap-4 md:grid-cols-3">
                  <div>
                    <h4 className="mb-1 text-sm font-medium text-foreground">Daily Calorie Goals</h4>
                    <p className="text-xs text-muted-foreground">Reference: {client?.eer ? `${client.eer} kcal` : 'N/A'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {isEditMode ? (
                      <LabeledInput
                        label=""
                        type="number"
                        min={0}
                        max={10000}
                        step="1"
                        value={clientGoal?.eerGoalCalories ?? ''}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const parsed = raw === '' ? null : parseFloat(raw);
                          shouldAutoRecalculateEerRef.current = true;
                          handleDataChange('eerGoalCalories', parsed);
                        }}
                        onWheel={(e) => e.currentTarget.blur()}
                        className="h-8 text-sm"
                        labelClassName="hidden"
                        placeholder="2000"
                      />
                    ) : (
                      <span className="text-sm font-semibold text-foreground">
                        {(() => {
                          const displayCalorieGoal = getDisplayCalorieGoalValue();
                          return displayCalorieGoal !== null ? `${Math.round(displayCalorieGoal)} kcal` : 'NA';
                        })()}
                      </span>
                    )}
                    {isEditMode && renderCalorieOverrideNotice('mt-2')}
                  </div>
                  <div />
                </div>
              )}
            </div>

            {/* Nutritional Goals Table */}
            <div className={compactView ? 'overflow-hidden rounded-lg border border-slate-200' : 'overflow-hidden rounded-lg border border-border/50 bg-muted/30'}>
              {!compactView && (
                <div className="border-b border-border/50 px-4 py-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-foreground">Nutritional Goals</h4>
                    {isEditMode && (
                      <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                        Set minimum and maximum values for each macronutrient
                      </p>
                    )}
                    {isTrafficLightSupportedFormula() && !isEditMode && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Client goal alignment with nutritional guidelines ({getFormulaDisplayName()} – not intake-based)
                      </p>
                    )}
                  </div>
                  {!canEditMacros() && (
                    <div className="flex items-center gap-2 text-amber-600 text-xs">
                      <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                      <span>Target calories required</span>
                    </div>
                  )}
            </div>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={compactView ? 'bg-transparent' : 'bg-muted/50'}>
                    <tr>
                      <th className="text-left py-2 px-4 text-xs font-medium text-muted-foreground">Nutrient</th>
                      <th className="text-left py-2 px-4 text-xs font-medium text-muted-foreground">Percentage</th>
                      <th className="text-left py-2 px-4 text-xs font-medium text-muted-foreground">Clinical Limit / Reference</th>
                      <th className="text-left py-2 px-4 text-xs font-medium text-muted-foreground">Client Goal</th>
                    </tr>
                  </thead>
                  <tbody className={compactView ? 'divide-y divide-slate-200' : 'divide-y divide-border/30'}>
                    {/* Protein Row */}
                    <tr className="hover:bg-muted/20">
                      <td className="py-3 px-4">
                        <span className="text-sm font-medium text-foreground">Protein</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-xs text-muted-foreground">
                          {getPercentageRange('protein')}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-xs text-blue-600 dark:text-blue-400 font-semibold">
                          {getReferenceRange('protein') || 'N/A'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {isEditMode && canEditMacros() ? (
                          <div className="flex gap-2 items-center">
                            <LabeledInput
                              label=""
                              type="number"
                              min={0}
                              max={1000}
                              step="0.1"
                              value={getEditableNutrientInputValue('protein', 'grams')}
                              onChange={(e) => {
                                const raw = e.target.value;
                                const v = parseNullableNumber(raw);
                                if (raw !== '' && v === null) return;
                                handleClientGoalChange('protein', 'grams', v);
                              }}
                              className="h-7 text-xs w-24"
                              labelClassName="hidden"
                              placeholder="46"
                            />
                            <span className="text-xs text-muted-foreground">g</span>
                            <LabeledInput
                              label=""
                              type="number"
                              min={0}
                              max={5000}
                              step="1"
                              value={getEditableNutrientInputValue('protein', 'kcal')}
                              onChange={(e) => {
                                const raw = e.target.value;
                                const v = parseNullableNumber(raw);
                                if (raw !== '' && v === null) return;
                                handleClientGoalChange('protein', 'kcal', v);
                              }}
                              className="h-7 text-xs w-24"
                              labelClassName="hidden"
                              placeholder="183"
                            />
                            <span className="text-xs text-muted-foreground">kcal</span>
                          </div>
                        ) : (
                          renderClientGoalWithStatus('protein', getClientGoalValue('protein'), isEditMode)
                        )}
                      </td>
                    </tr>

                    {/* Carbohydrate Row */}
                    <tr className="hover:bg-muted/20">
                      <td className="py-3 px-4">
                        <span className="text-sm font-medium text-foreground">Carbohydrate</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-xs text-muted-foreground">
                          {getPercentageRange('carbohydrate')}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-xs text-blue-600 dark:text-blue-400 font-semibold">
                          {getReferenceRange('carbohydrate') || 'N/A'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {isEditMode && canEditMacros() ? (
                          <div className="flex gap-2 items-center">
                            <LabeledInput
                              label=""
                              type="number"
                              min={0}
                              max={1000}
                              step="1"
                              value={getEditableNutrientInputValue('carbohydrates', 'grams')}
                              onChange={(e) => {
                                const raw = e.target.value;
                                const v = parseNullableNumber(raw);
                                if (raw !== '' && v === null) return;
                                handleClientGoalChange('carbohydrates', 'grams', v);
                              }}
                              className="h-7 text-xs w-24"
                              labelClassName="hidden"
                              placeholder="222"
                            />
                            <span className="text-xs text-muted-foreground">g</span>
                            <LabeledInput
                              label=""
                              type="number"
                              min={0}
                              max={5000}
                              step="1"
                              value={getEditableNutrientInputValue('carbohydrates', 'kcal')}
                              onChange={(e) => {
                                const raw = e.target.value;
                                const v = parseNullableNumber(raw);
                                if (raw !== '' && v === null) return;
                                handleClientGoalChange('carbohydrates', 'kcal', v);
                              }}
                              className="h-7 text-xs w-24"
                              labelClassName="hidden"
                              placeholder="889"
                            />
                            <span className="text-xs text-muted-foreground">kcal</span>
                          </div>
                        ) : (
                          renderClientGoalWithStatus('carbohydrates', getClientGoalValue('carbohydrates'), isEditMode)
                        )}
                      </td>
                    </tr>

                    {/* Total Fat Row */}
                    <tr className="hover:bg-muted/20">
                      <td className="py-3 px-4">
                        <span className="text-sm font-medium text-foreground">Total Fat</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-xs text-muted-foreground">
                          {getPercentageRange('total_fat')}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-xs text-blue-600 dark:text-blue-400 font-semibold">
                          {getReferenceRange('total_fat') || 'N/A'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {isEditMode && canEditMacros() ? (
                          <div className="flex gap-2 items-center">
                            <LabeledInput
                              label=""
                              type="number"
                              min={0}
                              max={1000}
                              step="0.1"
                              value={getEditableNutrientInputValue('totalFat', 'grams')}
                              onChange={(e) => {
                                const raw = e.target.value;
                                const v = parseNullableNumber(raw);
                                if (raw !== '' && v === null) return;
                                handleClientGoalChange('totalFat', 'grams', v);
                              }}
                              className="h-7 text-xs w-24"
                              labelClassName="hidden"
                              placeholder="56"
                            />
                            <span className="text-xs text-muted-foreground">g</span>
                            <LabeledInput
                              label=""
                              type="number"
                              min={0}
                              max={5000}
                              step="1"
                              value={getEditableNutrientInputValue('totalFat', 'kcal')}
                              onChange={(e) => {
                                const raw = e.target.value;
                                const v = parseNullableNumber(raw);
                                if (raw !== '' && v === null) return;
                                handleClientGoalChange('totalFat', 'kcal', v);
                              }}
                              className="h-7 text-xs w-24"
                              labelClassName="hidden"
                              placeholder="500"
                            />
                            <span className="text-xs text-muted-foreground">kcal</span>
                          </div>
                        ) : (
                          renderClientGoalWithStatus('totalFat', getClientGoalValue('totalFat'), isEditMode)
                        )}
                      </td>
                    </tr>

                    {/* Fiber Row */}
                    <tr className="hover:bg-muted/20">
                      <td className="py-3 px-4">
                        <span className="text-sm font-medium text-foreground">Fiber</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-xs text-muted-foreground">N/A</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-xs text-blue-600 dark:text-blue-400 font-semibold">
                          {getReferenceRange('dietary_fibre') || 'N/A'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {isEditMode && canEditMacros() ? (
                          <div className="flex gap-2 items-center">
                            <LabeledInput
                              label=""
                              type="number"
                              min={0}
                              max={100}
                              step="1"
                              value={getEditableNutrientInputValue('dietaryFibre', 'value')}
                              onChange={(e) => {
                                const raw = e.target.value;
                                const v = parseNullableNumber(raw);
                                if (raw !== '' && v === null) return;
                                handleClientGoalChange('dietaryFibre', 'value', v);
                              }}
                              className="h-7 text-xs w-24"
                              labelClassName="hidden"
                              placeholder="25"
                            />
                            <span className="text-xs text-muted-foreground">g/day</span>
                          </div>
                        ) : (
                          renderClientGoalWithStatus('dietaryFibre', getClientGoalValue('dietaryFibre'), isEditMode)
                        )}
                      </td>
                    </tr>

                    {!compactView && (
                      <>
                        {/* Water Row */}
                        <tr className="hover:bg-muted/20">
                          <td className="py-3 px-4">
                            <span className="text-sm font-medium text-foreground">Water</span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-xs text-muted-foreground">-</span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">
                              {getReferenceRange('water') || '2.5L - 3.0L'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            {isEditMode && canEditMacros() ? (
                              <div className="flex items-center gap-2">
                                <LabeledInput
                                  label=""
                                  type="number"
                                  min={0}
                                  max={10}
                                  step="0.1"
                                  value={getEditableNutrientInputValue('water', 'value')}
                                  onChange={(e) => {
                                    const raw = e.target.value;
                                    const v = parseNullableNumber(raw);
                                    if (raw !== '' && v === null) return;
                                    handleClientGoalChange('water', 'value', v);
                                  }}
                                  className="h-7 w-24 text-xs"
                                  labelClassName="hidden"
                                  placeholder="2.7"
                                />
                                <span className="text-xs text-muted-foreground">L/day</span>
                              </div>
                            ) : (
                              renderClientGoalWithStatus('water', getClientGoalValue('water'), isEditMode)
                            )}
                          </td>
                        </tr>

                        {/* Saturated Fat Row - Read Only */}
                        <tr className="hover:bg-muted/20">
                          <td className="py-3 px-4">
                            <span className="text-sm font-medium text-foreground">Sat Fat</span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-xs text-muted-foreground">
                              {getPercentageRange('saturated_fat', true)}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">
                              {getReferenceRange('saturated_fat') || 'N/A'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            {renderClientGoalWithStatus('saturatedFat', getClientGoalValue('saturatedFat'), false)}
                          </td>
                        </tr>

                        {/* Trans Fat Row - Read Only */}
                        <tr className="hover:bg-muted/20">
                          <td className="py-3 px-4">
                            <span className="text-sm font-medium text-foreground">Trans Fat</span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-xs text-muted-foreground">
                              {getPercentageRange('trans_fat', true)}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">
                              {getReferenceRange('trans_fat') || 'N/A'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            {renderClientGoalWithStatus('transFat', getTransFatValue(), false)}
                          </td>
                        </tr>

                        {/* Free Sugars Row - Read Only */}
                        <tr className="hover:bg-muted/20">
                          <td className="py-3 px-4">
                            <span className="text-sm font-medium text-foreground">Free Sugars</span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-xs text-muted-foreground">
                              {getPercentageRange('free_sugars', true)}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">
                              {getReferenceRange('free_sugars') || 'N/A'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            {renderClientGoalWithStatus('freeSugars', { primary: getFreeSugarsValue() }, false)}
                          </td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>

    <Dialog
      open={compactView && isGoalsModalOpen}
      onOpenChange={(open) => {
        if (!open) {
          closeGoalsEditorModal();
        }
      }}
    >
      <DialogContent className="max-w-[560px] overflow-hidden border border-slate-200 bg-white p-0">
        <DialogHeader className="border-b border-slate-200 px-4 py-3">
          <DialogTitle className="text-[16px] font-semibold text-slate-800">Edit Calories and Macros</DialogTitle>
          <DialogDescription className="pt-0.5 text-sm text-slate-500">
            Customize daily calorie and macronutrient goals
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] space-y-4 overflow-y-auto px-4 py-3">
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <h4 className="text-[16px] font-semibold text-slate-700">Daily Calorie Goal (EER, kcal)</h4>
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label="How EER is calculated"
                      className="inline-flex text-slate-400 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-1"
                    >
                      <Info className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs text-xs">
                    Uses the saved daily calorie target. If protein, carbohydrate, or total fat changes, the target updates from those energy-providing macros; fibre remains a separate intake target.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Input
              type="number"
              value={clientGoal?.eerGoalCalories ?? ''}
              disabled
              aria-describedby="eer-calculation-hint"
              className="h-9 border-slate-200 bg-slate-100 text-sm text-slate-600 disabled:cursor-not-allowed disabled:opacity-100"
            />
            <p id="eer-calculation-hint" className="text-sm text-slate-500">
              Automatically updates as the macro goals change.
            </p>
            {renderCalorieOverrideNotice()}
            {Math.abs(macroEnergyGap(getDisplayCalorieGoalValue() ?? 0, getModalMacroSummary().proteinG, getModalMacroSummary().carbsG, getModalMacroSummary().fatG)) > 9 && (
              <p className="text-sm text-amber-700" role="status">
                Macro targets provide {getModalMacroSummary().totalKcal} of {getDisplayCalorieGoalValue()} kcal/day.
                {' '}Difference: {Math.round(Math.abs((getDisplayCalorieGoalValue() ?? 0) - getModalMacroSummary().totalKcal))} kcal.
              </p>
            )}
          </div>

          <div className="h-px bg-slate-200" />

          <div className="space-y-3">
            <h4 className="text-[16px] font-semibold text-slate-700">Macronutrient Goals (grams)</h4>

            <div className="space-y-2">
              <h5 className="text-[14px] font-semibold text-slate-700">Protein (g/day)</h5>
              <Input
                type="number"
                min={0}
                max={1000}
                step="0.1"
                value={getEditableNutrientInputValue('protein', 'grams')}
                onChange={(e) => {
                  const raw = e.target.value;
                  const v = parseNullableNumber(raw);
                  if (raw !== '' && v === null) return;
                  handleClientGoalChange('protein', 'grams', v);
                }}
                className="h-9 text-sm"
              />
              <p className="text-sm text-slate-500">
                {`${getModalMacroSummary().proteinG}g = ${getModalMacroSummary().proteinKcal} kcal (${getModalMacroSummary().proteinPct.toFixed(1)}% of total calories)`}
              </p>
              {renderModalMacroHint('protein')}
            </div>

            <div className="space-y-2">
              <h5 className="text-[14px] font-semibold text-slate-700">Carbohydrate (g/day)</h5>
              <Input
                type="number"
                min={0}
                max={1000}
                step="1"
                value={getEditableNutrientInputValue('carbohydrates', 'grams')}
                onChange={(e) => {
                  const raw = e.target.value;
                  const v = parseNullableNumber(raw);
                  if (raw !== '' && v === null) return;
                  handleClientGoalChange('carbohydrates', 'grams', v);
                }}
                className="h-9 text-sm"
              />
              <p className="text-sm text-slate-500">
                {`${getModalMacroSummary().carbsG}g = ${getModalMacroSummary().carbsKcal} kcal (${getModalMacroSummary().carbsPct.toFixed(1)}% of total calories)`}
              </p>
              {renderModalMacroHint('carbohydrates')}
            </div>

            <div className="space-y-2">
              <h5 className="text-[14px] font-semibold text-slate-700">Total Fat (g/day)</h5>
              <Input
                type="number"
                min={0}
                max={1000}
                step="0.1"
                value={getEditableNutrientInputValue('totalFat', 'grams')}
                onChange={(e) => {
                  const raw = e.target.value;
                  const v = parseNullableNumber(raw);
                  if (raw !== '' && v === null) return;
                  handleClientGoalChange('totalFat', 'grams', v);
                }}
                className="h-9 text-sm"
              />
              <p className="text-sm text-slate-500">
                {`${getModalMacroSummary().fatG}g = ${getModalMacroSummary().fatKcal} kcal (${getModalMacroSummary().fatPct.toFixed(1)}% of total calories)`}
              </p>
              {renderModalMacroHint('totalFat')}
            </div>

            <div className="space-y-2">
              <h5 className="text-[14px] font-semibold text-slate-700">Fiber (g/day)</h5>
              <Input
                type="number"
                min={0}
                max={100}
                step="1"
                value={getEditableNutrientInputValue('dietaryFibre', 'value')}
                onChange={(e) => {
                  const raw = e.target.value;
                  const v = parseNullableNumber(raw);
                  if (raw !== '' && v === null) return;
                  handleClientGoalChange('dietaryFibre', 'value', v);
                }}
                className="h-9 text-sm"
              />
              <p className="text-sm text-slate-500">
                {`${getModalMacroSummary().fibreG}g/day — separate intake target`}
              </p>
              {renderModalMacroHint('dietaryFibre')}
            </div>
          </div>

        </div>

        <div className="flex items-center justify-end border-t border-slate-200 px-4 py-3">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={closeGoalsEditorModal}
              disabled={isSaving}
              className="h-9 text-sm text-slate-600"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleRecalculateMacros}
              disabled={isSaving}
              className="h-9 bg-cyan-500 px-4 text-sm hover:bg-cyan-600"
            >
              <Save className="mr-2 h-4 w-4" />
              Save changes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Dietary Preferences - Separate Card */}
    <Card
      className={
        compactView
          ? 'mb-8 rounded-3xl border border-slate-200 bg-white shadow-none'
          : 'mb-8 border-0 bg-gradient-to-br from-background to-blue/5 shadow-lg transition-all duration-300 hover:shadow-xl'
      }
    >
      <CardHeader className={compactView ? 'pb-4' : 'pb-4'}>
        <div className="flex items-center justify-between">
          <CardTitle
            className={
              compactView
                ? 'flex items-center text-[12px] font-medium leading-none text-slate-700'
                : 'flex items-center text-xl font-bold text-foreground'
            }
          >
            {compactView ? (
              <Coffee className="mr-1.5 h-3.5 w-3.5 text-cyan-500" />
            ) : (
              <Target className="mr-3 h-6 w-6 text-blue-600" />
            )}
            <span>Dietary Preferences</span>
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className={`inline-flex items-center text-slate-400 transition-colors hover:text-slate-600 ${
                      compactView ? 'ml-1 h-3.5 w-3.5' : 'ml-2 h-4 w-4'
                    }`}
                    aria-label="Dietary preferences scope info"
                  >
                    <Info className="h-full w-full" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p className="text-sm">{dietaryPreferencesTooltipText}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardTitle>
          <div className="flex items-center gap-2">
            {compactView ? (
              <button
                type="button"
                onClick={openPreferencesModal}
                className="text-[14px] font-medium text-cyan-500 transition-colors hover:text-cyan-600"
                data-analytics-ignore={onPreferencesEditOpen ? 'true' : undefined}
                data-demo-id="ai-plan-edit-preferences"
              >
                Edit Preferences
              </button>
            ) : !isPreferencesEditMode ? (
              <Button
                type="button"
                onClick={() => {
                  setIsPreferencesEditMode(true);
                  originalValuesRef.current = { ...clientGoal };
                  setPreferencesEditedFields(new Set());
                }}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
                data-demo-id="ai-plan-edit-preferences"
              >
                <Edit className="h-4 w-4" />
                Edit Preferences
              </Button>
            ) : (
              <>
                {!hideSaveButtons && (
                  <>
                    <Button
                      type="button"
                      onClick={handlePreferencesCancel}
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-2"
                    >
                      <X className="h-4 w-4" />
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={handlePreferencesSave}
                      disabled={isPreferencesSaving}
                      size="sm"
                      className="flex items-center gap-2"
                    >
                      {isPreferencesSaving ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                      {isPreferencesSaving ? 'Saving...' : 'Save'}
                    </Button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {compactView ? (
          <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2 md:gap-4 xl:grid-cols-4">
            <div className="space-y-1">
              <p className="text-[12px] font-medium tracking-[0.06em] text-slate-500">CUISINE</p>
              {getCuisineTags().length > 0 ? (
                <p className="text-[17px] leading-tight text-slate-700">
                  {getCuisineTags()
                    .map((tag) => tag.name)
                    .join(', ')}
                </p>
              ) : (
                <p className="text-[14px] text-slate-400">Not specified</p>
              )}
            </div>

            <div className="space-y-1">
              <p className="text-[12px] font-medium tracking-[0.06em] text-slate-500">PREFERENCES</p>
              {getPreferenceTags().length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {getPreferenceTags().map((tag) => (
                    <Tag
                      key={tag.id}
                      variant="info"
                      size="md"
                      startIcon={<Flag className="h-3.5 w-3.5" />}
                      className="bg-blue-50 px-1.5 py-0.5 text-[14px] font-medium text-blue-700"
                    >
                      {tag.name}
                    </Tag>
                  ))}
                </div>
              ) : (
                <p className="text-[14px] text-slate-400">None specified</p>
              )}
            </div>

            <div className="space-y-1">
              <p className="text-[12px] font-medium tracking-[0.06em] text-slate-500">ALLERGIES</p>
              {getAllergyTags().length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {getAllergyTags().map((tag) => (
                    <Tag
                      key={tag.id}
                      variant="danger"
                      size="md"
                      startIcon={<Ban className="h-3.5 w-3.5" />}
                      className="bg-red-50 px-1.5 py-0.5 text-[14px] font-medium text-red-600"
                    >
                      {tag.name}
                    </Tag>
                  ))}
                </div>
              ) : (
                <p className="text-[14px] text-slate-400">None specified</p>
              )}
            </div>

            <div className="space-y-1">
              <p className="text-[12px] font-medium tracking-[0.06em] text-slate-500">INTOLERANCES</p>
              {getFoodIntoleranceTags().length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {getFoodIntoleranceTags().map((tag) => (
                    <Tag
                      key={tag.id}
                      variant="warning"
                      size="md"
                      startIcon={<AlertTriangle className="h-3.5 w-3.5" />}
                      className="bg-amber-100 px-1.5 py-0.5 text-[14px] font-medium text-amber-700"
                    >
                      {tag.name}
                    </Tag>
                  ))}
                </div>
              ) : (
                <p className="text-[14px] text-slate-400">None specified</p>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {/* Cuisine Types */}
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-foreground">Cuisine Types</h4>
              {showInlinePreferencesEditor ? (
                <MultiSelect
                  options={cuisineSuggestions}
                  selected={clientGoal.cuisineType || []}
                  onChange={(selected) => handlePreferencesDataChange('cuisineType', selected)}
                  placeholder="Add cuisines..."
                  className="w-full"
                />
              ) : (
                <div>
                  {clientGoal.cuisineType && clientGoal.cuisineType.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {getCuisineTags().map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center rounded bg-secondary px-2 py-1 text-sm font-medium text-secondary-foreground"
                        >
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">Not specified</p>
                  )}
                </div>
              )}
            </div>

            {/* Allergens */}
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-foreground">Allergens</h4>
              {showInlinePreferencesEditor ? (
                <MultiSelect
                  options={allergySuggestions}
                  selected={clientGoal.allergies || []}
                  onChange={(selected) => handlePreferencesDataChange('allergies', selected)}
                  placeholder="Add allergens..."
                  className="w-full"
                />
              ) : (
                <div>
                  {clientGoal.allergies && clientGoal.allergies.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {getAllergyTags().map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center rounded bg-secondary px-2 py-1 text-sm font-medium text-secondary-foreground"
                        >
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">None specified</p>
                  )}
                </div>
              )}
            </div>

            {/* Lifestyle Preferences */}
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-foreground">Preferences</h4>
              {showInlinePreferencesEditor ? (
                <MultiSelect
                  options={preferenceSuggestions}
                  selected={clientGoal.preferences || []}
                  onChange={(selected) => handlePreferencesDataChange('preferences', selected)}
                  placeholder="Add preferences..."
                  className="w-full"
                />
              ) : (
                <div>
                  {clientGoal.preferences && clientGoal.preferences.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {getPreferenceTags().map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center rounded bg-secondary px-2 py-1 text-sm font-medium text-secondary-foreground"
                        >
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">None specified</p>
                  )}
                </div>
              )}
            </div>

            {/* Food Intolerances */}
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-foreground">Food Intolerances</h4>
              {showInlinePreferencesEditor ? (
                <MultiSelect
                  options={foodIntoleranceSuggestions}
                  selected={clientGoal.foodIntolerances || []}
                  onChange={(selected) => handlePreferencesDataChange('foodIntolerances', selected)}
                  placeholder="Add intolerances..."
                  className="w-full"
                />
              ) : (
                <div>
                  {clientGoal.foodIntolerances && clientGoal.foodIntolerances.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {getFoodIntoleranceTags().map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center rounded bg-secondary px-2 py-1 text-sm font-medium text-secondary-foreground"
                        >
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">None specified</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>

    <Dialog
      open={compactView && isPreferencesModalOpen}
      onOpenChange={(open) => {
        if (!open) {
          closePreferencesModal();
        }
      }}
    >
      <DialogContent className="max-w-[560px] overflow-hidden border border-slate-200 bg-white p-0">
        <DialogHeader className="border-b border-slate-200 px-4 py-3">
          <DialogTitle className="text-xl font-semibold text-slate-800">Edit Dietary Preferences</DialogTitle>
          <DialogDescription className="pt-0.5 text-sm text-slate-500">
            Manage cuisine preferences, dietary preferences, allergens, and intolerances.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[62vh] space-y-4 overflow-y-auto px-4 py-3">
          <div className="space-y-2">
            <h4 className="text-[16px] font-semibold text-slate-700">Cuisine Preferences</h4>
            <div className="space-y-0">
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input
                  value={cuisineModalInput}
                  onChange={(e) => setCuisineModalInput(e.target.value)}
                  onFocus={() => setShowCuisineSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowCuisineSuggestions(false), 120)}
                  placeholder="e.g., Mediterranean, Asian, Italian..."
                  className="h-9 text-sm [&_fieldset]:border-0"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addCuisineFromModalInput();
                    }
                  }}
                />
                <Button type="button" onClick={addCuisineFromModalInput} className="h-9 min-w-[72px] bg-cyan-500 text-sm font-normal hover:bg-cyan-600">
                  + Add
                </Button>
              </div>
              {showCuisineSuggestions || cuisineModalInput.trim() ? (
                <div className="max-h-36 overflow-y-auto rounded-b-md border border-slate-200 border-t-0 bg-white">
                  {getVisibleSuggestions(cuisineSuggestions, clientGoal.cuisineType, cuisineModalInput).length > 0 ? (
                    getVisibleSuggestions(cuisineSuggestions, clientGoal.cuisineType, cuisineModalInput).map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleCuisineAddition(option)}
                        className="block w-full border-b border-slate-100 px-3 py-1.5 text-left text-sm text-slate-700 last:border-b-0 hover:bg-slate-50"
                      >
                        {option.name}
                      </button>
                    ))
                  ) : (
                    <p className="px-3 py-2 text-sm text-slate-500">No matching cuisine options</p>
                  )}
                </div>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-2">
              {(clientGoal.cuisineType || []).map((id) => {
                const name = cuisineSuggestions.find((option) => option.id === id)?.name || id;
                return (
                  <span key={id} className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
                    {name}
                    <button
                      type="button"
                      className="text-slate-500 hover:text-slate-700"
                      onClick={() =>
                        handlePreferencesDataChange(
                          'cuisineType',
                          (clientGoal.cuisineType || []).filter((item) => item !== id)
                        )
                      }
                    >
                      ×
                    </button>
                  </span>
                );
              })}
            </div>
          </div>

          <div className="h-px bg-slate-200" />

          <div className="space-y-2">
            <h4 className="text-[16px] font-semibold text-slate-700">Dietary Preferences</h4>
            <div className="space-y-0">
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input
                  value={preferenceModalInput}
                  onChange={(e) => setPreferenceModalInput(e.target.value)}
                  onFocus={() => setShowPreferenceSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowPreferenceSuggestions(false), 120)}
                  placeholder="e.g., Vegetarian, Vegan, High Protein..."
                  className="h-9 text-sm [&_fieldset]:border-0"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addPreferenceFromModalInput();
                    }
                  }}
                />
                <Button type="button" onClick={addPreferenceFromModalInput} className="h-9 min-w-[72px] bg-cyan-500 text-sm font-normal hover:bg-cyan-600">
                  + Add
                </Button>
              </div>
              {showPreferenceSuggestions || preferenceModalInput.trim() ? (
                <div className="max-h-36 overflow-y-auto rounded-b-md border border-slate-200 border-t-0 bg-white">
                  {getVisibleSuggestions(preferenceSuggestions, clientGoal.preferences, preferenceModalInput).length > 0 ? (
                    getVisibleSuggestions(preferenceSuggestions, clientGoal.preferences, preferenceModalInput).map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handlePreferenceAddition(option)}
                        className="block w-full border-b border-slate-100 px-3 py-1.5 text-left text-sm text-slate-700 last:border-b-0 hover:bg-slate-50"
                      >
                        {option.name}
                      </button>
                    ))
                  ) : (
                    <p className="px-3 py-2 text-sm text-slate-500">No matching dietary preference options</p>
                  )}
                </div>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-2">
              {(clientGoal.preferences || []).map((id) => {
                const name = preferenceSuggestions.find((option) => option.id === id)?.name || id;
                return (
                  <span key={id} className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
                    {name}
                    <button
                      type="button"
                      className="text-slate-500 hover:text-slate-700"
                      onClick={() =>
                        handlePreferencesDataChange(
                          'preferences',
                          (clientGoal.preferences || []).filter((item) => item !== id)
                        )
                      }
                    >
                      ×
                    </button>
                  </span>
                );
              })}
            </div>
          </div>

          <div className="h-px bg-slate-200" />

          <div className="space-y-2">
            <h4 className="text-[16px] font-semibold text-slate-700">Allergens (Critical)</h4>
            <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50/60 px-3 py-2 text-xs text-red-700">
              <svg className="h-4 w-4 shrink-0 text-red-400" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M9 16.5C13.1421 16.5 16.5 13.1421 16.5 9C16.5 4.85786 13.1421 1.5 9 1.5C4.85786 1.5 1.5 4.85786 1.5 9C1.5 13.1421 4.85786 16.5 9 16.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 12V9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 6H9.00833" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>Meals will strictly avoid all ingredients clients are allergic to.</span>
            </div>
            <div className="space-y-0">
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input
                  value={allergyModalInput}
                  onChange={(e) => setAllergyModalInput(e.target.value)}
                  onFocus={() => setShowAllergySuggestions(true)}
                  onBlur={() => setTimeout(() => setShowAllergySuggestions(false), 120)}
                  placeholder="e.g., Peanuts, Shellfish, Dairy..."
                  className="h-9 text-sm [&_fieldset]:border-0"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addAllergyFromModalInput();
                    }
                  }}
                />
                <Button type="button" onClick={addAllergyFromModalInput} className="h-9 min-w-[72px] bg-cyan-500 text-sm font-normal hover:bg-cyan-600">
                  + Add
                </Button>
              </div>
              {showAllergySuggestions || allergyModalInput.trim() ? (
                <div className="max-h-36 overflow-y-auto rounded-b-md border border-slate-200 border-t-0 bg-white">
                  {getVisibleSuggestions(allergySuggestions, clientGoal.allergies, allergyModalInput).length > 0 ? (
                    getVisibleSuggestions(allergySuggestions, clientGoal.allergies, allergyModalInput).map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleAllergyAddition(option)}
                        className="block w-full border-b border-slate-100 px-3 py-1.5 text-left text-sm text-slate-700 last:border-b-0 hover:bg-slate-50"
                      >
                        {option.name}
                      </button>
                    ))
                  ) : (
                    <p className="px-3 py-2 text-sm text-slate-500">No matching allergen options</p>
                  )}
                </div>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-2">
              {getAllergyTags().length > 0 ? (
                getAllergyTags().map((tag) => (
                  <span key={tag.id} className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
                    {tag.name}
                    <button
                      type="button"
                      className="text-slate-500 hover:text-slate-700"
                      onClick={() =>
                        handlePreferencesDataChange(
                          'allergies',
                          (clientGoal.allergies || []).filter((item) => item !== tag.id)
                        )
                      }
                    >
                      ×
                    </button>
                  </span>
                ))
              ) : (
                <p className="text-sm text-slate-500">No allergens recorded.</p>
              )}
            </div>
          </div>

          <div className="h-px bg-slate-200" />

          <div className="space-y-2">
            <h4 className="text-[16px] font-semibold text-slate-700">Intolerances and Sensitivities</h4>
            <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs text-amber-800">
              <svg className="h-4 w-4 shrink-0 text-amber-500" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M9 16.5C13.1421 16.5 16.5 13.1421 16.5 9C16.5 4.85786 13.1421 1.5 9 1.5C4.85786 1.5 1.5 4.85786 1.5 9C1.5 13.1421 4.85786 16.5 9 16.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 12V9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 6H9.00833" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>Meals will avoid or limit ingredients that clients are intolerant or sensitive to.</span>
            </div>
            <div className="space-y-0">
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input
                  value={intoleranceModalInput}
                  onChange={(e) => setIntoleranceModalInput(e.target.value)}
                  onFocus={() => setShowIntoleranceSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowIntoleranceSuggestions(false), 120)}
                  placeholder="e.g., Lactose, Gluten, FODMAPs..."
                  className="h-9 text-sm [&_fieldset]:border-0"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addIntoleranceFromModalInput();
                    }
                  }}
                />
                <Button type="button" onClick={addIntoleranceFromModalInput} className="h-9 min-w-[72px] bg-cyan-500 text-sm font-normal hover:bg-cyan-600">
                  + Add
                </Button>
              </div>
              {showIntoleranceSuggestions || intoleranceModalInput.trim() ? (
                <div className="max-h-36 overflow-y-auto rounded-b-md border border-slate-200 border-t-0 bg-white">
                  {getVisibleSuggestions(
                    foodIntoleranceSuggestions,
                    clientGoal.foodIntolerances,
                    intoleranceModalInput
                  ).length > 0 ? (
                    getVisibleSuggestions(
                      foodIntoleranceSuggestions,
                      clientGoal.foodIntolerances,
                      intoleranceModalInput
                    ).map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => addFoodIntolerance(option)}
                        className="block w-full border-b border-slate-100 px-3 py-1.5 text-left text-sm text-slate-700 last:border-b-0 hover:bg-slate-50"
                      >
                        {option.name}
                      </button>
                    ))
                  ) : (
                    <p className="px-3 py-2 text-sm text-slate-500">No matching intolerance options</p>
                  )}
                </div>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-2">
              {(clientGoal.foodIntolerances || []).map((id) => {
                const name = foodIntoleranceSuggestions.find((option) => option.id === id)?.name || id;
                return (
                  <span key={id} className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
                    {name}
                    <button
                      type="button"
                      className="text-slate-500 hover:text-slate-700"
                      onClick={() =>
                        handlePreferencesDataChange(
                          'foodIntolerances',
                          (clientGoal.foodIntolerances || []).filter((item) => item !== id)
                        )
                      }
                    >
                      ×
                    </button>
                  </span>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-4 py-3">
          <Button type="button" variant="ghost" onClick={closePreferencesModal} className="text-sm text-slate-600">
            Cancel
          </Button>
          <Button
            type="button"
            onClick={savePreferencesFromModal}
            disabled={isPreferencesSaving}
            className="h-9 bg-cyan-500 px-4 text-sm hover:bg-cyan-600"
          >
            {isPreferencesSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save Preferences
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </div>
  );
});

DietaryIntakeGoals.displayName = 'DietaryIntakeGoals';
